<template>
  <div class="contacts-table-dashboard greedy">
    <ScrollArea
      ref="contactsTableScrollbar"
      @top="loadPrevious"
      @bottom="loadNext" >
      <h2 class="dashboard_title">
        Contacts
      </h2>

      <Forbidden v-if="noPermission" />

      <!-- *************************************** -->
      <!-- no contacts -->
      <!-- *************************************** -->
      <template v-else-if="!forceSpinner && noContacts && !searchQuery && !savedFilters.length">
        <!-- no contacts exist -->
        <div class="no-contacts-wrapper">
          <span class="sb2-icon contact-circle size-32"/>
          <h5>Add contacts</h5>
          <div class="description">
            You don't have any contacts {{ branchFilterCurrentItem.id ? 'for the selected number' : 'in your organisation' }}.
          </div>
          <div class="options flex column">
            <b-button
              class="flex center-all"
              @click="openCreateContactModal">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.blackPrimary"
                :size="8"/>
              <span>New</span>
            </b-button>

            <span
              class="import flex center-all mt-3"
              @click="$refs.ImportModal.show()" >
              <SBIcon
                class="mr-2"
                :icon="SBIconList.IMPORT"
                color="#54626C"
                :size="12"/>
              <span>Import</span>
            </span>
          </div>
        </div>
      </template>

      <!-- *************************************** -->
      <!-- has contacts -->
      <!-- *************************************** -->
      <template v-else>
        <!-- *************************************** -->
        <!-- table tools -->
        <!-- *************************************** -->
        <div
          ref="tableTools"
          class="table-tools d-flex align-items-center justify-content-between sticky-top">
          <div
            ref="leftToolsRef"
            class="tools-left d-flex align-items-center">
            <SearchInput
              ref="searchInput"
              v-model="searchQuery"
              placeholder="Search contacts..."
              @input="markDebounce"
              @change="searchContacts" />
            <!-- *************************************** -->
            <!-- Tag filtering -->
            <!-- *************************************** -->
            <div class="sb2-wrapper">
              <b-dropdown
                ref="filterMenuDropdown"
                class="filter-dropdown sb2-bordered sb2-hoverable sb2-focusable "
                :class="{ 'pr-0': selectedFilters.length, 'add-y-scroll': showScrollbarOnFilter, 'sb-focused-btn': focusFilterBtn }"
                offset="0 10"
                no-caret
                @show="focusFilterBtn = true"
                @hidden="cancelFilterChanges"
                @hide="beforeDropdownHides" >
                <template #button-content >
                  <span
                    class="filter-button-main-content" >
                    <span class="sb2-icon filter size-12" />
                    Filter
                  </span>
                  <span
                    v-if="savedFilters.length"
                    class="filter-button-filter-count">
                    {{ savedFilters.length }}
                  </span>
                </template>
                <div>
                  <div class="header">
                    <h5>Filters</h5>
                    <b-button
                      variant="primary"
                      class="flex center-all save"
                      :disabled="!selectedFilters.length && !filterHasChanges"
                      @click="saveFilter" >
                      <span>Save</span>
                    </b-button>
                    <b-button
                      variant="secondary"
                      class="flex center-all clear"
                      @click="clearFilter" >
                      <span>Clear</span>
                    </b-button>
                  </div>
                  <div class="content">
                    <!-- Empty filter -->
                    <template v-if="!selectedFilters.length">
                      <div class="flex column flex-align-center initial-view">
                        <span
                          class="empty-icon" />
                        <p>Use filters to segment and find specific<br>contacts and their data.</p>
                        <span
                          class="flex center-all action"
                          @click.stop="addFilter" >
                          <SBIcon
                            :icon="SBIconList.PLUS"
                            :color="SBColors.bluePrimary"
                            class="p-0 m-0 mr-2"
                            :border-radius="0"
                            :size="8" />
                          Add filter</span>
                      </div>
                    </template>
                    <!-- Filter builder -->
                    <template v-else>
                      <div
                        v-for="(filter, index) in selectedFilters"
                        :key="index"
                        class="filter-wrapper">

                        <template v-if="index">
                          <multiselect
                            v-model="operator"
                            class="flex-options hide-tags no-selected-background filter-operator"
                            :options="filterOperators"
                            :multiple="false"
                            :show-labels="false"
                            :show-no-results="false"
                            :close-on-select="true"
                            :clear-on-select="false"
                            :preserve-search="true"
                            :preselect-first="true"
                            :allow-empty="false"
                            :searchable="false" >
                            <template
                              slot="option"
                              slot-scope="props">
                              <div class="flex flex-align-center">
                                <p class="greedy ellipsis p-0 m-0">{{ props.option }}</p>
                              </div>
                            </template>
                          </multiselect>
                        </template>

                        <div class="filter-row">
                          <SBIcon
                            class="close"
                            :icon="SBIconList.CLOSE_THIN"
                            :color="SBColors.blackPrimary"
                            :hover-size="16"
                            :size="8"
                            @click="removeFilterOption(index)"
                            @click.native.stop.prevent />
                          <multiselect
                            v-model="selectedFilters[index].type"
                            placeholder="Select"
                            class="flex-options hide-tags no-selected-background filter-type no-shrink"
                            :options="filterTypes"
                            label="text"
                            track-by="value"
                            :multiple="false"
                            :show-labels="false"
                            :show-no-results="false"
                            :close-on-select="true"
                            :clear-on-select="false"
                            :preserve-search="true"
                            :preselect-first="true"
                            :allow-empty="false"
                            :searchable="false"
                            @input="typeChange(index)" >
                            <template
                              slot="singleLabel"
                              slot-scope="props"
                              class="tag-scroll-wrapper">
                              <SBIcon
                                :icon="props.option.icon"
                                :color="SBColors.blackPrimary"
                                class="p-0 m-0 force-show mr-2"
                                :border-radius="0"
                                :size="14"/>
                              <p class="greedy ellipsis p-0 m-0">{{ props.option.text }}</p>
                            </template>

                            <template
                              slot="option"
                              slot-scope="props">
                              <div class="flex flex-align-center">
                                <SBIcon
                                  v-if="props.option.icon"
                                  :icon="props.option.icon"
                                  :color="SBColors.blackPrimary"
                                  class="p-0 m-0 force-show mr-2"
                                  :border-radius="0"
                                  :size="14"/>
                                <p class="greedy ellipsis p-0 m-0">{{ props.option.text }}</p>
                                <SBIcon
                                  :icon="SBIconList.DONE_TICK"
                                  :color="SBColors.bluePrimary"
                                  :size="9"/>
                              </div>
                            </template>
                          </multiselect>

                          <multiselect
                            v-model="selectedFilters[index].comparison"
                            placeholder="Select"
                            class="flex-options hide-tags no-selected-background filter-comparison no-shrink"
                            :options="comparisonTypes"
                            label="text"
                            track-by="value"
                            :multiple="false"
                            :show-labels="false"
                            :show-no-results="false"
                            :close-on-select="true"
                            :clear-on-select="false"
                            :preserve-search="true"
                            :preselect-first="true"
                            :allow-empty="false"
                            :searchable="false" >
                            <template
                              slot="option"
                              slot-scope="props">
                              <div class="flex flex-align-center">
                                <p>{{ props.option.text }}</p>
                                <SBIcon
                                  :icon="SBIconList.DONE_TICK"
                                  :color="SBColors.bluePrimary"
                                  :size="9"/>
                              </div>
                            </template>
                          </multiselect>

                          <SBTagMultiselect
                            v-if="selectedFilters[index] && selectedFilters[index].type && selectedFilters[index].type.value === 'tag'"
                            v-model="selectedFilters[index].value"
                            class="greedy min-width" />
                          <SBNumberMultiselect
                            v-else-if="selectedFilters[index] && selectedFilters[index].type && selectedFilters[index].type.value === 'business'"
                            v-model="selectedFilters[index].value"
                            class="greedy min-width" />
                          <div
                            v-else
                            class="date-wrapper flex">
                            <div class="date relative">
                              <el-date-picker
                                ref="datePickerStartRef"
                                v-model="selectedFilters[index].dateRangeStart"
                                type="date"
                                align="left"
                                format="'From' d MMM, yyyy"
                                placeholder="Start date"
                                prefix-icon=""
                                :editable="false"
                                :picker-options="startPickerOptions"
                                @focus="focusAfterDelay(index)"
                                @blur="datePickerOpened = false" />
                              <SBIcon
                                class="down-arrow"
                                :icon="SBIconList.DOWN_ARROW"
                                :color="SBColors.greyPrimary"
                                :size="11"
                                :hover-size="30"/>
                            </div>

                            <div class="date relative">
                              <el-date-picker
                                ref="datePickerEndRef"
                                v-model="selectedFilters[index].dateRangeEnd"
                                type="date"
                                align="left"
                                format="'To' d MMM, yyyy"
                                placeholder="End date"
                                :editable="false"
                                :picker-options="endPickerOptions"
                                @focus="focusAfterDelay(index)"
                                @blur="datePickerOpened = false" />
                              <SBIcon
                                class="down-arrow"
                                :icon="SBIconList.DOWN_ARROW"
                                :color="SBColors.greyPrimary"
                                :size="11"
                                :hover-size="30"/>
                            </div>
                          </div>

                        </div>
                      </div>
                      <span
                        class="flex flex-align-center action"
                        :class="{ disabled: selectedFilters.length >= contactFilterLimits.maxNumberOfBlocks }"
                        @click.stop="addFilter" >
                        <SBIcon
                          :icon="SBIconList.PLUS"
                          :color="SBColors.bluePrimary"
                          class="p-0 m-0 mr-2"
                          :border-radius="0"
                          :size="8" />
                        Add filter</span>
                    </template>
                  </div>
                </div>
              </b-dropdown>

              <!-- *************************************** -->
              <!-- SORT -->
              <!-- *************************************** -->
              <b-dropdown
                class="sort-dropdown sb2-bordered sb2-hoverable sb2-focusable"
                :class="{ 'sb-focused-btn': focusSortBtn }"
                offset="0 10"
                no-caret
                @show="focusSortBtn = true"
                @hidden="focusSortBtn = false" >
                <template #button-content>
                  <span class="sort-prefix">Sort by&nbsp;</span>
                  <span class="sort-type">{{ sortType && sortType.text ? sortType.text : '' }}</span>
                </template>
                <div>
                  <h5>Sort by</h5>
                  <div class="content">
                    <p class="subtitle">All options</p>
                    <div class="flex" style="column-gap: 10px;">
                      <multiselect
                        v-model="sortType"
                        placeholder="Select an option"
                        class="flex-options hide-tags no-selected-background"
                        :options="sortOptions"
                        label="text"
                        track-by="value"
                        :multiple="false"
                        :show-labels="false"
                        :show-no-results="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        :preserve-search="true"
                        :preselect-first="true"
                        :allow-empty="false"
                        :searchable="false"
                        @select="reSort" >
                        <template
                          slot="singleLabel"
                          slot-scope="props">
                          <div class="flex flex-align-center">
                            <SBIcon
                              v-if="props.option.prefixIcon"
                              :icon="props.option.prefixIcon"
                              :color="SBColors.blackPrimary"
                              class="p-0 m-0 force-show mr-2"
                              :border-radius="0"
                              :size="14"/>
                            <p class="greedy ellipsis p-0 m-0">{{ props.option.text }}</p>
                          </div>
                        </template>
                        <template
                          slot="option"
                          slot-scope="props" >
                          <div class="flex flex-align-center">
                            <SBIcon
                              v-if="props.option.prefixIcon"
                              :icon="props.option.prefixIcon"
                              :color="SBColors.blackPrimary"
                              class="p-0 m-0 force-show mr-2"
                              :size="14"/>
                            <p class="greedy p-0 m-0">{{ props.option.text }}</p>
                            <SBIcon
                              :icon="SBIconList.DONE_TICK"
                              :color="SBColors.bluePrimary"
                              :size="9"/>
                          </div>
                        </template>
                      </multiselect>

                      <multiselect
                        v-model="sortDirection"
                        placeholder="Select an option"
                        class="flex-options hide-tags no-selected-background"
                        :options="sortType ? sortType.direction : []"
                        label="text"
                        track-by="value"
                        :multiple="false"
                        :show-labels="false"
                        :show-no-results="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        :preserve-search="true"
                        :preselect-first="true"
                        :allow-empty="false"
                        :searchable="false"
                        @select="reSort" >
                        <template
                          slot="singleLabel"
                          slot-scope="props">
                          <div class="flex flex-align-center">
                            <SBIcon
                              v-if="props.option.prefixIcon"
                              :icon="props.option.prefixIcon"
                              :color="SBColors.blackPrimary"
                              :rotate="props.option.prefixIconRotation"
                              class="p-0 m-0 force-show mr-2"
                              :border-radius="0"
                              :size="14"/>
                            <p class="greedy ellipsis p-0 m-0">{{ props.option.text }}</p>
                          </div>
                        </template>
                        <template
                          slot="option"
                          slot-scope="props">
                          <div class="flex flex-align-center">
                            <SBIcon
                              v-if="props.option.prefixIcon"
                              :icon="props.option.prefixIcon"
                              :color="SBColors.blackPrimary"
                              :rotate="props.option.prefixIconRotation"
                              class="p-0 m-0 force-show mr-2"
                              :size="14"/>
                            <p class="greedy">{{ props.option.text }}</p>
                            <SBIcon
                              :icon="SBIconList.DONE_TICK"
                              :color="SBColors.bluePrimary"
                              :size="9"
                              class="mr-0"/>
                          </div>
                        </template>
                      </multiselect>
                    </div>
                  </div>
                </div>
              </b-dropdown>
            </div>
          </div>
          <!-- Tag filtering END -->
          <div
            class="tools-right">
            <!-- Initial buttons -->
            <b-button
              v-if="!broadcastMode"
              class="flex center-all no-wrap"
              @click="startBroadcastMode">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.BROADCAST"
                :color="SBColors.blackPrimary"
                :size="16"/>
              <span>Send broadcast</span>
            </b-button>
            <b-button
              v-if="!selectAllGhost && !selectedRowsCounter && !broadcastMode"
              class="flex center-all"
              :disabled="progressRunning"
              @click="$refs.ImportModal.show()">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.IMPORT"
                :color="SBColors.blackPrimary"
                :size="12"/>
              <span>Import</span>
            </b-button>
            <b-button
              v-if="!selectAllGhost && !selectedRowsCounter && !broadcastMode"
              class="flex center-all"
              @click="openCreateContactModal">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.blackPrimary"
                :size="8"/>
              <span>New</span>
            </b-button>

            <!-- ACTION buttons -->
            <span
              v-if="!broadcastMode"
              @mouseenter="preloadTags()">
              <b-dropdown
                v-if="selectAllGhost || selectedRowsCounter"
                right
                offset="0 5"
                class="manage-tags-dropdown actions-btn filter-dropdown sb2-bordered sb2-hoverable sb2-focusable"
                :class="{ 'sb-focused-btn': focusManageTagsBtn }"
                no-caret
                @show="showTagManagerDropdown()"
                @hide="forceTagManagerClose()"
                @hidden="hideTagManagerDropdown()" >
                <template #button-content >
                  <span
                    class="filter-button-main-content flex center-all no-wrap" >
                    <SBIcon
                      class="mr-2"
                      :icon="SBIconList.TAG"
                      :color="SBColors.blackPrimary"
                      :size="12"/>
                    <span>Manage tags</span>
                  </span>
                </template>
                <template slot-scope="{ hide }">
                  <div class="header">
                    <h5>Edit tags</h5>
                    <b-button
                      variant="primary"
                      class="flex center-all save"
                      :disabled="!tagManagerChangesPending"
                      @click="saveTagChangesPrecheck(hide)" >
                      <span>Save</span>
                    </b-button>
                    <b-button
                      variant="secondary"
                      class="flex center-all clear"
                      @click="forceTagManagerClose(hide)" >
                      <span>Cancel</span>
                    </b-button>
                  </div>
                  <div class="tag-seach-wrapper">
                    <SearchInput
                      ref="searchTagInput"
                      v-model="tagsManagerSearchQuery"
                      placeholder="Type to search or create new tag..."
                      full-width
                      no-prefix-icon
                      @input="searchTags" />
                  </div>
                  <ScrollArea
                    ref="tagManagerScrollbar"
                    class="tag-manager-scroll-area"
                    @bottom="loadMoreManagerTags" >
                    <template v-if="tagsManagerSearchQuery && !tagsManagerLoading && !tagExists">
                      <p class="button-dropdown-label no-wrap">New tag</p>
                      <div
                        class="tag flex flex-align-center show-hand"
                        @click.stop="createTag" >
                        <SBIcon
                          class="mr-2"
                          :icon="SBIconList.TAG"
                          :color="SBColors.blackPrimary"
                          :size="12"/>
                        <p class="name ellipsis greedy">Create <b>{{ tagsManagerSearchQuery }}</b></p>
                      </div>
                    </template>
                    <p
                      v-if="!tagsManagerLoading && availableManagerTags.length"
                      class="button-dropdown-label no-wrap">All tags</p>
                    <template
                      v-if="!selectAllGhost && !tagsManagerLoading">
                      <div
                        v-for="(tagItem, index) in sortedTagCountEdit.filter(st => st.name.indexOf(tagsManagerSearchQuery) >= 0)"
                        :key="`tag-count-${index}`"
                        class="tag flex flex-align-center">
                        <b-form-checkbox
                          v-model="tagsCountCheckboxEdit[tagItem.id]"
                          class="header-checkbox"
                          :class="{ grey: tagItem.count < selectedContactsCounter }"
                          @change="setPartialToAll($event, tagItem.count < selectedContactsCounter, tagItem)" />
                        <SBIcon
                          class="mr-2"
                          :icon="SBIconList.TAG"
                          :color="SBColors.blackPrimary"
                          :size="12"/>
                        <p class="name ellipsis greedy">{{ tagItem.name }}</p>
                        <p
                          v-if="tagItem.count"
                          class="count no-shrink">{{ tagItem.count }} contact{{ tagItem.count === 1 ? '' : 's' }}</p>
                      </div>
                    </template>
                    <div
                      v-for="(tag, index) in availableManagerTags.filter(at => !Object.values(tagCount).map(t => t.id).includes(at.id))"
                      :key="`tag-${index}`"
                      class="tag flex flex-align-center">
                      <b-form-checkbox
                        v-model="tagsCountCheckboxEdit[tag.id]"
                        class="header-checkbox"
                        :class="{ grey: (!selectAllGhost && tag.count < selectedContactsCounter && editedTagCheckbox[tag.id] !== true) || (selectAllGhost && tag.count < selectedContactsCounter) }"
                        @change="editTagCheckbox($event, tag.count < selectedContactsCounter, tag)" />
                      <SBIcon
                        class="mr-2"
                        :icon="SBIconList.TAG"
                        :color="SBColors.blackPrimary"
                        :size="12"/>
                      <p class="name ellipsis greedy pr-2">{{ tag.name }}</p>
                      <p
                        v-if="tag.count && (tag.count - (Number(negativeTagCount[tag.id]) || 0)) && tagsCountCheckboxEdit[tag.id] !== false && selectAllGhost"
                        class="count no-shrink">{{ negativeTagCount[tag.id] ? tag.count - negativeTagCount[tag.id] : tag.count }} contact{{ (tag.count === 1) || (negativeTagCount[tag.id] && tag.count - negativeTagCount[tag.id] === 1) ? '' : 's' }}</p>
                      <p
                        v-else-if="tagsCountCheckboxEdit[tag.id] && tag.count && (tag.count - (Number(negativeTagCount[tag.id]) || 0)) "
                        class="count no-shrink">{{ selectedContactsCounter }} contact{{ selectedContactsCounter === 1 ? '' : 's' }}</p>
                    </div>
                    <Spinner
                      v-if="tagsManagerLoading"
                      class="mt-2"
                      :size="16"
                      :thickness="2" />
                  </ScrollArea>
                </template>
              </b-dropdown>
            </span>
            <!-- <b-button
              v-if="(selectAllGhost || selectedRowsCounter) && !showActionDropdown"
              class="flex center-all no-wrap"
              @click="() => {}">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.EXPORT"
                :color="SBColors.blackPrimary"
                :size="12"/>
              <span>Export as CSV</span>
            </b-button> -->

            <!--  add '!showActionDropdown &&' in v-if once delete will be in the more actions dropdown for small screens. That will hapen once we add more options to the menu. -->
            <b-button
              v-if="selectedRowsCounter === 1 && !broadcastMode"
              class="flex center-all no-wrap ml-2"
              @click="confirmDeleteSingleContact">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.TRASH"
                :color="SBColors.blackPrimary"
                :size="12"/>
              <span>Delete</span>
            </b-button>

            <!-- ACTION dropdown for small screens -->
            <!-- Remove false once more than one option is in the menu. Also hide delete from default wiev at the same time -->
            <b-dropdown
              v-if="false && (selectAllGhost || selectedRowsCounter) && showActionDropdown && !broadcastMode"
              id="dropdown-left"
              ref="rightToolsActionsRef"
              text="Left align"
              right
              offset="0 5"
              class="tools-right actions-btn filter-dropdown fit-width sb2-bordered sb2-hoverable sb2-focusable"
              :class="{ 'sb-focused-btn': focusActionsBtn }"
              no-caret
              @show="focusActionsBtn = true"
              @hidden="focusActionsBtn = false" >
              <template #button-content >
                <span
                  class="filter-button-main-content no-wrap" >
                  More actions
                  <SBIcon
                    class="ml-2"
                    :icon="SBIconList.DOWN_ARROW"
                    :color="SBColors.greyPrimary"
                    :size="11"/>
                </span>
              </template>
              <p class="button-dropdown-label no-wrap">All actions</p>
              <!-- <div
                class="flex flex-align-center item"
                @click="startBroadcastMode" >
                <SBIcon
                  class="mr-2 no-wrap"
                  :icon="SBIconList.BROADCAST"
                  :color="SBColors.blackPrimary"
                  :size="15"/>
                <span>Send broadcast</span>
              </div> -->
              <!-- <div
                class="flex flex-align-center item"
                @click="() => {}">
                <SBIcon
                  class="mr-2 no-wrap"
                  :icon="SBIconList.EXPORT"
                  :color="SBColors.blackPrimary"
                  :size="14"/>
                <span>Export as CSV</span>
              </div>
              <div
                class="flex flex-align-center item"
                @click="() => {}">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.TRASH"
                  :color="SBColors.blackPrimary"
                  :size="14"/>
                <span>Delete</span>
              </div> -->
              <div
                v-if="selectedRowsCounter === 1"
                class="flex flex-align-center item"
                @click="confirmDeleteSingleContact">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.TRASH"
                  :color="SBColors.blackPrimary"
                  :size="12"/>
                <span>Delete</span>
              </div>
            </b-dropdown>
          </div>
        </div>

        <div
          v-if="initialContactsLoaded && !contactsAppended.length && !loadingContacts && !forceSpinner"
          class="no-data flex column center-all">
          <span
            v-if="searchQueryAfterDebounce || savedFilters.length"
            class="wrapper flex center-all">
            <span class="emoji">😐</span>
            <p>
              We couldn't find anything for
              <strong v-if="searchQueryAfterDebounce">{{ searchQueryAfterDebounce }}</strong>
              <span v-if="searchQueryAfterDebounce && savedFilters.length"> and</span>
              <span v-if="savedFilters.length"> selected filter{{ savedFilters.length > 1 ? 's' : '' }}</span>
            </p>
          </span>
          <span
            v-if="savedFilters.length"
            class="clear-filters-btn"
            @click="clearFilter" >Clear filter{{ savedFilters.length > 1 ? 's' : '' }}</span>
        </div>

        <div
          v-else-if="forceSpinner || (!contactsAppended.length && loadingContacts)"
          class="spinner-padding" />

        <!-- Data grid -->
        <div
          v-if="!forceSpinner && initialContactsLoaded && contactsAppended.length"
          ref="table-area"
          class="sb-table-area clickable bottom-40">

          <div class="sb-table-header sticky-top sticky-second flex flex-align-center">
            <template v-if="!selectAll && !shiftActive && !selectedRowsCounter">
              <span class="name-header show-arrow flex flex-align-center">
                <!-- v-show="!searchQuery" -->
                <b-form-checkbox
                  ref="ForceAllRef"
                  :checked="false"
                  class="header-checkbox p-0 m-0"
                  @change="forceSelectAll" />
                <!-- <b-form-checkbox
                  v-show="searchQuery"
                  v-model="selectPage"
                  class="header-checkbox p-0 m-0"
                  @change="selectContactsOnPage($event)" /> -->
                <p>Name</p>
              </span>
              <span class="show-arrow number-header">Number</span>
              <span class="show-arrow tag-header" />
              <span class="show-arrow date-header" >Date</span>
            </template>

            <!-- All selected checkbox -->
            <template v-else>
              <span
                class="selected-counter full-width name-header show-arrow flex flex-align-center"
                :class="{ grey: !selectAll && !selectedRowsCounter }" >
                <!-- !searchQuery &&  -->
                <!-- <b-form-checkbox
                  v-if="((!selectAll && !allSelectedManually && selectedRowsCounter) || (selectAll && deselectedRowsCounter))"
                  :checked="true"
                  class="header-checkbox grey"
                  @change="restateSelection($event)" /> -->
                <!-- !searchQuery &&  -->
                <!-- <b-form-checkbox
                  v-show="!selectAll && allSelectedManually"
                  :checked="true"
                  class="header-checkbox"
                  @change="clearSelections" /> -->
                <!-- !searchQuery &&  -->
                <!-- <b-form-checkbox
                  v-show="selectAll && !allSelectedManually && !deselectedRowsCounter"
                  v-model="selectAll"
                  class="header-checkbox" /> -->
                <!-- <b-form-checkbox
                  v-show="searchQuery"
                  v-model="selectPage"
                  class="header-checkbox"
                  @change="selectContactsOnPage($event)" /> -->
                <!-- {{ (selectAll ? (deselectedRowsCounter ? maxContacts - deselectedRowsCounter : 'All') : (maxContacts === selectedRowsCounter ? 'All': selectedRowsCounter)) | bigDigits }}&nbsp;<span class="suffix">selected</span></span> -->
                <b-form-checkbox
                  v-if="selectAll"
                  ref="selectAllRef"
                  checked
                  class="header-checkbox"
                  :class="{ grey: (deselectedRowsCounter && selectAll) || (!selectAll && !allSelectedManually && selectedRowsCounter) }"
                  @change="restateSelection($event)" />
                <b-form-checkbox
                  v-if="!selectAll"
                  :checked="!!selectedRowsCounter"
                  class="header-checkbox"
                  :class="{ grey: (deselectedRowsCounter && selectAll) || (!selectAll && !allSelectedManually && selectedRowsCounter) }"
                  @change="restateSelection($event)" />
                {{ (selectAll ? (deselectedRowsCounter ? maxContacts - deselectedRowsCounter : 'All') : (maxContacts === selectedRowsCounter ? 'All': selectedRowsCounter)) | bigDigits }}&nbsp;<span class="suffix">selected</span></span>
            </template>
          </div>

          <div
            v-if="contactsAppended.length && !loadingContacts"
            class="sb-table-info flex flex-align-center" >
            CONTACTS ({{ (meta && meta.total ? meta.total : 0) | bigDigits }})
          </div>

          <RecycleScroller
            v-slot="{ item, index, active }"
            ref="recycle-scroller-wrapper"
            page-mode
            emit-update
            class="scroller"
            :items="contactsAppended"
            :min-item-size="52"
            :buffer="300"
            key-field="id"
            @update="recycleScrollerUpdate" >
            <div
              :ref="`recycle-scroller-${index}`"
              class="sb-table-item flex flex-align-center"
              :class="{
                'checkbox-state': (!selectAll && ctrlActive) || (!selectAll && shiftActive) || (!selectAll && selectedRowsCounter) || (!selectAll && broadcastMode),
                'key-mode-active': ctrlActive || shiftActive,
                'all-checkbox-state': selectAll,
                'checked': checkedContacts[item.id] || (selectAll && !deselectedContacts[item.id]),
                'invisible': item.size === 0,
              }"
              @click="selectRow($event, item, index)" >
              <span class="sb-table-name-wrapper flex flex-align-center">
                <span
                  class="show-on-all-checkbox-state checkbox-wrapper flex flex-align-center overlay-checkbox no-shrink"
                  @click.stop >

                  <b-form-checkbox
                    :ref="`all-checkbox-${item.id}`"
                    v-model="allSelectedCheckboxModel[item.id]"
                    :class="'checkbox-with-description no-shrink all-selected-mode'"
                    @input="allCheckboxChange($event, item.id, index)" />
                </span>

                <span
                  class="show-on-name-hover show-on-checkbox-state hide-on-all-checkbox-state checkbox-wrapper flex flex-align-center overlay-checkbox no-shrink"
                  @click.stop >
                  <b-form-checkbox
                    :ref="`checkbox-${item.id}`"
                    v-model="checkedContacts[item.id]"
                    :checked="selectAll"
                    :class="'checkbox-with-description checkbox-mode'"
                    @change="checkUnchecForContact($event, item.id, index)" />
                </span>
                <SBAvatar
                  class="hide-on-name-hover hide-on-checkbox-state hide-on-all-checkbox-state no-shrink avatar flex center-all"
                  size="s-button"
                  :show-data="true"
                  :show-client="true"
                  :character-count="1"
                  :person="item"
                  :hide-avatar-image="true" />

                <!-- Name column cell -->
                <p
                  class="sb-table-name-column ellipsis"
                  :class="{ 'muted': !item.name }"
                  :inner-html.prop="(!item.name ? 'No name' : item.name) | boldMatch(searchQuery)" />
              </span>

              <!-- Phone column cell -->
              <span
                class="sb-table-phone-column ellipsis"
                :inner-html.prop="item.phone | phoneFormat | boldMatch(searchQuery)" />

              <!-- Tag column cell -->
              <div
                :key="`${index}-role`"
                :class="['sb-table-tag-column ellipsis', `hover-${index} flex flex-align-center`, { 'checked': checkedContacts[item.id] || (selectAll && !deselectedContacts[item.id]) }, {'default-cursor': selectAll || shiftActive || selectedRowsCounter}]" >
                <div
                  class="sb2-tag-pill-wrapper">
                  <template v-if="!item.has_messaging_consent">
                    <span
                      :id="'status-unsubscribed-' + item.id"
                      class="status-unsubscribed sb2-tag-pill pr-0">
                      Messaging disabled
                      <SBIcon
                        :icon="SBIconList.INFO"
                        :color="SBColors.redPrimary"
                        :size="12" />
                    </span>
                    <b-popover
                      :target="'status-unsubscribed-' + item.id"
                      container="contacts-table-wrapper"
                      triggers="hover"
                      placement="bottom"
                      custom-class="big red">
                      <h1 class="title">Contact opted-out from receiving messages</h1>
                      <p>Sending messages to this contact is disabled.</p>
                    </b-popover>
                  </template>
                  <span
                    v-for="(tag, indexTag) in item.tags"
                    :id="'tag-pill-' + item.id + indexTag"
                    :key="tag.id"
                    class="sb2-tag-pill"
                    :class="{ 'no-edit': !tag.editable }">
                    <img
                      v-if="tag.icons"
                      :src="tag.icons.dark"
                      style="height: 11px; margin-right: 4px;" >
                    {{ tag.name }}
                    <b-popover
                      v-if="tag.name.toLowerCase() === 'undeliverable'"
                      :target="'tag-pill-' + item.id + indexTag"
                      triggers="hover"
                      placement="top"
                      custom-class="big blue-href">
                      <h1 class="title">Undeliverable WhatsApp Messages</h1>
                      <p>
                        It was not possible to send a message to this contact. One possible reason could be that the recipient is not on WhatsApp. Learn more about undeliverable messages
                        <a
                          href="https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/#other-errors"
                          target="_blank">here</a>. Try sending a message again to see if the contact will receive it.</p>
                    </b-popover>
                  </span>
                </div>
              </div>

              <!-- Date column cell -->
              <div
                :key="`${index}-action`"
                :class="[`sb-table-date-column hover-${index} flex flex-align-center justify-end more-column ellipsis`, 'last', { 'checked': checkedContacts[item.id] || (selectAll && !deselectedContacts[item.id]) }, {'default-cursor': selectAll || shiftActive || selectedRowsCounter}]" >
                <SBIcon
                  class="show-on-hover2"
                  :icon="SBIconList.ARROW_THIN_TIP_RIGHT"
                  color="#9DAEB4"
                  :size="14"/>
                <span class="hide-on-hover2 ellipsis" >{{ item.created_at | tableDate }}</span>
              </div>
            </div>
          </RecycleScroller>
        </div>
      </template>

      <Spinner
        v-if="forceSpinner || loadingContacts || (contacts.meta.current_page < contacts.meta.last_page && initialContactsLoaded)"
        style="height: 38px;"
        class="my-3 full-width"
        :size="20"
        :thickness="2" />
      <div
        v-else-if="broadcastMode"
        style="padding-bottom: 74px !important;" />
      <div
        v-else
        class="pb-4" />
    </ScrollArea>

    <!-- *************************************** -->
    <!-- modals -->
    <!-- *************************************** -->
    <CreateContactModal
      ref="createContactModal"
      :preselect-business-id="branchFilterCurrentItem.id"
      @contact-create-success="onContactCreateSuccess"
      @contact-create-error="onContactCreateError"/>

    <ImportModal
      ref="ImportModal" />

    <BroadcastModal
      ref="broadcastModalRef"
      :class="{ hide: createNewModalVisible }"
      :selected-contacts="selectedContactsCounter"
      :deselected-contacts="Object.keys(deselectedContacts)"
      @create-new-template="createNewTemplate"
      @broadcast-sent="resetBroadcast" />

    <MessageTemplateModal
      ref="messageTemplateModalRef"
      :static-modal="true"
      :business-id="selectedBroadcastNumber ? selectedBroadcastNumber.id : ''"
      @hidden="createNewModalVisible = false"
      @abort="closeModals" />

    <!-- *************************************** -->
    <!-- Contact details panel -->
    <!-- *************************************** -->
    <transition name="overlay-right">
      <div
        v-if="showDetails"
        v-click-outside="closePanel"
        class="contact-details-overlay">
        <PanelDetails
          class="content"/>
        <PanelMenu
          :menu-acts-as-toggle="true"
          class="menu"
          @hide="closePanel" />
      </div>
    </transition>

    <!-- *************************************** -->
    <!-- Broadcast bottom action -->
    <!-- *************************************** -->
    <transition name="broadcast-selector">
      <div
        v-if="broadcastMode"
        class="broadcast-selector-content flex center-all" >
        <span
          class="broadcast-count"
          :class="{ ok: selectedContactsCounter && selectedContactsCounter <= maxBroadcastCalc, nok: selectedContactsCounter && selectedContactsCounter > maxBroadcastCalc }" >{{ selectedContactsCounter }}</span>
        <span> of </span>
        <span>{{ maxBroadcastCalc | bigDigits }}</span>
        <span class="recepients"> recipients</span>
        <SBIcon
          id="broadcast-info"
          class="ml-1"
          :icon="SBIconList.INFO"
          :color="SBColors.greyPrimary"
          :size="12"/>
        <b-popover
          target="broadcast-info"
          custom-class="broadcast-limits"
          triggers="hover"
          placement="top">
          <div>
            <h1>WhatsApp messaging limits</h1>
            <p>Your phone number is allowed to have {{ maxBroadcastCalc | bigDigits }} business-initiated conversations (with {{ maxBroadcastCalc | bigDigits }} unique customers) in a rolling 24-hour period. Learn more about WhatsApp limits <a href="https://developers.facebook.com/docs/whatsapp/messaging-limits/" target="_blank">here</a>.</p>
          </div>
        </b-popover>
        <!-- Number select popover -->
        <b-button
          id="select-number"
          class="flex flex-align-center ml-3"
          @click="() => {}" >
          <span>From</span>
          <VNodes :vnodes="getNumberIcon(selectedBroadcastNumber, 'number-icon')"/>
          <p>{{ selectedBroadcastNumber.name }}</p>
          <SBIcon
            class=""
            style="margin-left: 12px;"
            :icon="SBIconList.DOWN_ARROW"
            :color="SBColors.greyPrimary"
            :size="11"/>
        </b-button>
        <b-popover
          ref="branchFilterPopover"
          target="select-number"
          triggers="click"
          placement="top"
          custom-class="broadcast-selector-popover interactive-popover organisation-panel-popover hide-arrow"
          @show="startBranchSelect"
          @hide="endBranchSelect" >
          <SelectBranch
            v-click-outside="closePopover"
            title="Send from"
            :only-available="true"
            :selected-branch="selectedBroadcastNumber"
            @branch-selected="broadcastBranchSelected" />
        </b-popover>
        <!-- Number select popover END -->
        <span id="start-broadcast-wrapper">
          <b-button
            id="send-broadcast"
            class="flex center-all"
            style="margin-left: 12px;"
            :disabled="!selectedContactsCounter || selectedContactsCounter > maxBroadcastCalc || broadcastRunning"
            @click="showBroadcastModal" >
            <SBIcon
              :icon="SBIconList.BROADCAST"
              :color="SBColors.blackPrimary"
              :size="14" />
            <span class="ml-2">Send broadcast</span>
          </b-button>
        </span>
        <b-popover
          v-if="!selectedContactsCounter || selectedContactsCounter > maxBroadcastCalc || broadcastRunning"
          target="start-broadcast-wrapper"
          triggers="hover"
          placement="bottom"
          custom-class="basic-popover fit-width" >
          {{ broadcastRunning ? 'You can send only one broadcast at a time' : (!selectedContactsCounter ? 'Select contacts to send a broadcast' : 'Deselect contacts to send a broadcast') }}
        </b-popover>
        <SBIcon
          class="show-hand"
          style="margin-left: 12px;"
          :icon="SBIconList.CLOSE_THIN"
          :color="SBColors.greyPrimary"
          :hover-icon-color="SBColors.blackPrimary"
          :size="10"
          :hover-size="20"
          @click="cancelBroadcast" />
      </div>
    </transition>
  </div>

</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import ClickOutside from 'v-click-outside';
import { debounce } from 'debounce';
import Multiselect from 'vue-multiselect';
import { DatePicker } from 'element-ui';

import api from '@/helpers/api';
import SearchableList from '../../common/SearchableList';
import SBIcon from '../../common/Icon';
import SBIconList from '../../../helpers/lists/sb-icon-list';
import SBColors from '../../../helpers/lists/colors';
import ContactMenu from './partials/ContactMenu';
import ContactDetailsControl from '../contact_details/Control';
import CreateContactModal from './modals/CreateContactModal';
import ImportModal from './modals/ImportModal';
import Spinner from '../../common/Spinner';
import ToasterMixin from '@/mixins/Toaster';
import ScrollArea from '@/components/common/ScrollArea';
import SBAvatar from '../../common/Avatar';
import SearchInput from '@/components/business/widgets/SearchInput';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import NoPermissionMixin from '@/mixins/AccessPermission';
import PanelDetails from '@/components/business/contact_details/partials/PanelDetails';
import PanelMenu from '@/components/business/contact_details/partials/PanelMenu';
import SBTagMultiselect from '../../common/SBTagMultiselect';
import SBNumberMultiselect from '../../common/SBNumberMultiselect';
import ProgressStates from '@/helpers/lists/progressStates';
import JuneMixin from '@/mixins/June';
import FilterSingle from '@/components/common/FilterSingle';
import SelectBranch from '@/components/business/branch/SelectBranch';
import { getNumberIcon } from '@/helpers/lists/branchIcons';
import VNodes from '@/components/common/VNodes';
import BroadcastModal from './modals/BroadcastModal';
import MessageTemplateModal from '@/components/business/settings/modals/MessageTemplate.modal';

const branchFilterAllBranchesItem = {
  id: '',
  name: 'All contacts',
  logo: '',
  displayName: 'All contacts',
  iconIndex: null,
};

export default {
  name: 'ContactsTable',
  directives: {
    ClickOutside: ClickOutside.directive,
  },
  components: {
    CreateContactModal,
    ContactMenu,
    SBIcon,
    SearchableList,
    Multiselect,
    Spinner,
    ScrollArea,
    SBAvatar,
    SearchInput,
    PanelMenu,
    PanelDetails,
    SBTagMultiselect,
    SBNumberMultiselect,
    elDatePicker: DatePicker,
    ImportModal,
    SelectBranch,
    VNodes,
    BroadcastModal,
    MessageTemplateModal,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
    NoPermissionMixin,
    JuneMixin,
  ],
  data() {
    return {
      broadcastNumber: null,
      broadcastMode: false,
      numberSearchValue: '',
      selectedBroadcastNumber: null,
      createNewModalVisible: false,
      startPickerOptions: {
        disabledDate: this.disabledStartDates,
      },
      endPickerOptions: {
        disabledDate: this.disabledEndDates,
      },
      SBIconList,
      SBColors,
      allSelectedCheckboxModel: {},
      headerSpaceAvailable: null,
      spaceCalculatorTimeout: null,
      showActionDropdown: false,
      selectedContacts: [],
      selectedTags: {},
      selectedFilters: [],
      savedFilters: [],
      filterHasChanges: false,
      contactsAppended: [],
      // Used for keeping data for checked contacts and tags they have set. We can't refference contacts array directly because depending on paging and filter we might not have all data.
      trackTags: {},
      trackTagsRemove: {},
      tagCount: {},
      negativeTagCount: {},
      tagsCountCheckbox: {},
      tagsCountCheckboxEdit: {},
      sortedTagCount: [],
      sortedTagCountEdit: [],
      editedTagCheckbox: {},
      forceTagReload: false,
      dataBackup: {},
      tagsBackup: {},
      maxContacts: 0,
      selectPage: false,
      preventRecount: false,
      dateRangeStart: null,
      dateRangeEnd: null,
      forceSpinner: false,
      searchQuery: '',
      searchQueryAfterDebounce: '', // debounce that we are using is causing the search text to be be applied before the request method is triggered so this is the temp fix. We should switch to vue-debounce lib instead of js debounce lib we are now using.
      page: 1,
      limit: 50,
      errorMessage: '',
      finalizeProgressBar: false,
      focusFilterBtn: false,
      focusActionsBtn: false,
      focusSortBtn: false,
      datePickerOpened: false,
      pageSelected: false,
      allSelected: false,
      finalizeExportProgressBar: false,
      exportsWaiting: [],
      exportsExpanded: false,
      debounceActive: false,
      clickedContact: null,
      availableTags: [],
      tagPage: 0,
      maxTagPages: null,
      tagManagerPage: 0,
      maxManagerTagPages: null,
      tagsManagerSearchQuery: '',
      lastTagsManagerSearchQuery: '',
      availableManagerTags: [],
      tagsSearchQuery: '',
      loadingTagsForId: null,
      tagsLoading: false,
      tagsManagerLoading: false,
      showCheckboxForIndex: null,
      checkedContacts: {},
      deselectedContacts: {},
      selectAll: false,
      selectAllGhost: false,
      focusedDatePicker: null,
      // Check if using event.shiftKey instead of this.shiftActive will bring any benefits because shift active does not set if user is not focused on window when he presses shift. But shiftActive is still important to show all the checkboxes initially
      shiftActive: false,
      ctrlActive: false,
      lastCheckedIndex: null,
      preventUncheck: false,
      showDetails: false,
      partialEdited: {},
      filterTypes: [
        {
          text: 'Tag',
          value: 'tag',
          icon: SBIconList.TAG,
        },
        {
          text: 'Number',
          value: 'business',
          icon: SBIconList.PHONE_OUTLINE,
        },
        {
          text: 'Date',
          value: 'date',
          icon: SBIconList.CALENDAR,
        },
      ],
      comparisonTypes: [
        {
          text: 'Is',
          value: 'is',
        },
        {
          text: 'Isn\'t',
          value: 'isnot',
        },
      ],
      filterOperators: [
        'and',
        'or',
      ],
      showScrollbarOnFilter: false,
      operator: 'and',
      operatorSaved: 'and',
      sortType: null,
      sortDirection: null,
      sortOptions: [
        {
          text: 'Name',
          value: 'name',
          prefixIcon: SBIconList.TT,
          direction: [
            {
              text: 'Sort A to Z',
              value: 'ASC',
              prefixIcon: SBIconList.ARROW_DOWN,
            },
            {
              text: 'Sort Z to A',
              value: 'DESC',
              prefixIcon: SBIconList.ARROW_DOWN,
              prefixIconRotation: 180,
            },
          ],
        },
        {
          text: 'Date',
          value: 'date',
          prefixIcon: SBIconList.CALENDAR,
          direction: [
            {
              text: 'Sort 1 to 9',
              value: 'ASC',
              prefixIcon: SBIconList.ARROW_DOWN,
            },
            {
              text: 'Sort 9 to 1',
              value: 'DESC',
              prefixIcon: SBIconList.ARROW_DOWN,
              prefixIconRotation: 180,
            },
          ],
        },
        {
          text: 'Phone number',
          value: 'number',
          prefixIcon: SBIconList.PHONE_OUTLINE,
          direction: [
            {
              text: 'Sort 1 to 9',
              value: 'ASC',
              prefixIcon: SBIconList.ARROW_DOWN,
            },
            {
              text: 'Sort 9 to 1',
              value: 'DESC',
              prefixIcon: SBIconList.ARROW_DOWN,
              prefixIconRotation: 180,
            },
          ],
        },
      ],
      tagFilterSearchFn(searchTerm) {
        // returns function
        return item => item.name && item.name.toLowerCase().indexOf(searchTerm) !== -1;
      },
      // branch filter data
      branchFilterCurrentItem: branchFilterAllBranchesItem,
      branchFilterMenuIsOpen: false,
      branchFilterSearchFn(searchTerm) {
        // returns function
        return item => item.name && item.name.toLowerCase().indexOf(searchTerm) !== -1;
      },
      initialContactsLoaded: false,
      meta: null,
      focusManageTagsBtn: false,
      ProgressStates,
    };
  },
  computed: {
    ...mapGetters({
      contacts: 'contact/organisationContactPageList',
      loadingContacts: 'contact/loadingContacts',
      contactTags: 'contactTag/list',
      apiError: 'contact/apiError',
      branchesRaw: 'organisation/currentOrganisationBusinesses',
      getterCtrlPanelDisplayed: 'contactDetails/ctrlPanelDisplayedGet',
      addContact: 'contactDetails/addContact',
      deleteContact: 'contactDetails/deleteContact',
      currentOrganisation: 'organisation/currentOrganisation',
      activeSection: 'contactDetails/activeSection',
      contactFilterLimits: 'system/contactFilterLimits',
      progressActions: 'organisation/progressActions',
      numberIntegrationMap: 'organisation/numberIntegrationMap',
      maxBroadcastNumbers: 'system/maxBroadcastNumbers',
      permissionDenied: 'user/permissionDenied',
    }),
    broadcastRunning() {
      if (!Object.values(this.progressActions).length) {
        return false;
      }
      return Object.values(this.progressActions).some(pa => pa.type === 'broadcast' && pa.state === ProgressStates.RUNNING);
    },
    maxBroadcastCalc() {
      const availableMessagesOnNumber = this.selectedBroadcastNumber && this.selectedBroadcastNumber.whatsapp ? this.selectedBroadcastNumber.whatsapp.daily_message_limit - this.selectedBroadcastNumber.whatsapp.rolling_24h_sent : 0;
      return this.maxBroadcastNumbers > availableMessagesOnNumber ? (availableMessagesOnNumber > 0 ? availableMessagesOnNumber : 0) : this.maxBroadcastNumbers;
    },
    canAddContact() {
      return this.$hasPermissionInOrganisation('can_add_contact');
    },
    canDeleteContact() {
      return this.$hasPermissionInOrganisation('can_delete_contact');
    },
    hasManageContactsPermission() {
      return this.$hasPermissionInOrganisation('can_edit_contact');
    },
    hasSendNotificationPermission() {
      return this.$hasPermissionInOrganisation('send_notifications');
    },
    isPermissionDenied() {
      return this.permissionDenied.message === 'Unauthorized IP - Access Denied';
    },
    selectedRowsCounter() {
      return Object.values(this.checkedContacts).filter(ccv => ccv).length;
    },
    selectedContactsCounter() {
      return this.selectAll ? (this.deselectedRowsCounter ? this.maxContacts - this.deselectedRowsCounter : this.maxContacts) : this.selectedRowsCounter;
    },
    deselectedRowsCounter() {
      return Object.values(this.deselectedContacts).filter(ccv => ccv).length;
    },
    /** When user checks all the contacts by clicking each individual one. */
    allSelectedManually() {
      return this.maxContacts === this.selectedRowsCounter;
    },
    noPermission() {
      return !this.checkPermission('can_view_all_contacts');
    },
    // shorthand
    organisationId() {
      return this.$route.params.organisationId;
    },
    uniqueTagsList() {
      return [...this.$lodash.uniqBy(this.contactTags, 'name')];
    },
    rowClickEnabled() {
      return this.shiftActive || !!this.selectedRowsCounter || this.selectAll;
    },
    selectedTagsIds() {
      return Object.values(this.selectedTags).flat();
    },
    selectedTagsValues() {
      return Object.keys(this.selectedTags);
    },
    // selectedTagsName() {
    //   const tagNames = this.contactTags.reduce((filtered, tag) => {
    //     if (this.selectedTagsIds.includes(tag.id)) {
    //       filtered.push(tag.name);
    //     }

    //     return filtered;
    //   }, []);
    //   let buttonText = tagNames[0];
    //   if (tagNames.length > 1) {
    //     buttonText = `${tagNames.length} selected`;
    //   }
    //   return buttonText;
    // },
    // list of branches with added displayName
    branches() {
      return this.branchesRaw.map((b, index) => ({
        id: b.id,
        name: b.name,
        logo: b.logo,
        displayName: `${b.name} (${b.phone})`,
        iconIndex: index % 10,
      }));
    },
    // branches lookup since we're not using models to keep data consistent
    branchesById() {
      const lookup = {};

      this.branches.forEach((b) => {
        lookup[b.id] = b;
      });
      return lookup;
    },
    branchFilterItemList() {
      return [branchFilterAllBranchesItem, ...this.branches];
    },
    // tableFields() {
    //   const tFields = [
    //     // commented-out until pusher is sorted
    //     // {
    //     //   key: 'selectedContact',
    //     //   label: '',
    //     //   class: 'col-checkbox',
    //     // },
    //     {
    //       key: 'name',
    //       label: 'Name',
    //       class: 'col-name',
    //     },
    //     {
    //       key: 'phone',
    //       label: 'Number',
    //       class: 'col-phone',
    //     },
    //     {
    //       key: 'tags',
    //       label: '',
    //       class: 'col-tags',
    //     },
    //     // {
    //     //   key: 'conversation_status',
    //     //   label: 'Folder',
    //     // },
    //     // {
    //     //   key: 'status',
    //     //   label: '',
    //     //   class: 'col-status',
    //     // },
    //     // {
    //     //   key: 'created_at',
    //     //   label: 'Created on',
    //     //   class: 'col-created-at',
    //     // },
    //     {
    //       key: 'actions',
    //       label: '',
    //       class: 'col-actions',
    //     },
    //   ];

    //   return tFields;
    // },
    noContacts() {
      // true only if filtering is not set, contacts are not loading and we have 0 results
      return this.initialContactsLoaded &&
        !this.debounceActive &&
        !this.loadingContacts &&
        !this.meta.total &&
        !this.searchQuery &&
        this.selectedTagsValues.length < 1;
    },
    progressRunning() {
      const runningImport = Object.values(this.progressActions).find(pa => pa.type === 'import' && pa.state === this.ProgressStates.RUNNING);
      return !!runningImport;
    },
    searchIsNumber() {
      const regex = /^[+\-()0-9\s]+$/;
      return regex.test(this.searchQuery);
    },
    tagExists() {
      return this.availableManagerTags.findIndex(amt => amt.name.toLowerCase() === this.tagsManagerSearchQuery.toLowerCase()) >= 0;
    },
    tagManagerChangesPending() {
      const onlyCheckedCheckboxes = Object.fromEntries(Object.entries(this.tagsCountCheckboxEdit).filter(e => e[1]));
      return Object.keys(this.partialEdited).length || !this.$lodash.isEqual({ ...onlyCheckedCheckboxes }, { ...this.tagsCountCheckbox });
    },
    numbersToFilter() {
      const businessesWithEmoji = this.branchesRaw.map((number) => {
        const item = {
          id: number.id,
          name: number.name,
          phone: number.phone,
          icon: number.emoji,
          emoji: number.emoji,
          whatsapp: number.whatsapp,
        };
        if (this.numberIntegrationMap[number.id] && this.numberIntegrationMap[number.id].length === 1) {
          delete item.icon;
          item.integration = this.numberIntegrationMap[number.id][0];
        } else if (this.numberIntegrationMap[number.id] && this.numberIntegrationMap[number.id].length) {
          delete item.icon;
          item.integrationCount = this.numberIntegrationMap[number.id].length;
        }
        return item;
      });
      return businessesWithEmoji;
    },
    /** List of items filtered by the search term to be shown on the list. */
    // searchResults() {
    //   if (this.numberSearchValue) {
    //     return this.numbers.filter.filter(item => (
    //       item[this.label]
    //         ? (
    //           item[this.label].toLowerCase().indexOf(this.numberSearchValue.toLowerCase()) !== -1 ||
    //           (item[this.subLabel] && item[this.subLabel].toLowerCase().indexOf(this.numberSearchValue.toLowerCase()) !== -1)
    //         ) : item.toLowerCase().indexOf(this.numberSearchValue.toLowerCase()) !== -1
    //     ));
    //   }
    //   return this.items;
    // },
  },
  watch: {
    selectedFilters: {
      handler() {
        this.filterHasChanges = !this.$lodash.isEqual(this.savedFilters, this.selectedFilters);
        this.setFilterMaxHeight();
      },
      deep: true,
    },
    searchQuery() {
      this.selectPage = false;
      this.selectAll = false;

      if (!this.searchQuery) {
        this.$router.replace({ query: { ...this.$route.query, s: undefined } });
      } else if (this.$route.query.s !== this.searchQuery) {
        this.$router.replace({ query: { ...this.$route.query, s: this.searchQuery } });
      }
    },
    addContact() {
      this.fetchContact({ contact_id: this.addContact })
        .then((response) => {
          if (response.status === 200) {
            if (this.searchQuery && !(response.data.name.indexOf(this.searchQuery) >= 0 || response.data.phone.indexOf(this.searchQuery) >= 0)) {
              // Doesn't fit to search filter
              return;
            }

            const fitIndex = this.contactsAppended.findIndex(c => c.name && /^\w/.test(c.name.toString()) && c.name.toString().toUpperCase() > response.data.name.toString().toUpperCase());

            if (fitIndex >= 0) {
              this.contactsAppended.splice(fitIndex, 0, response.data);
            } else if (this.meta.current_page === this.meta.last_page) {
              this.contactsAppended = [...this.contactsAppended, response.data];
            }
          }
        });
    },
    $route(newRoute, previousRoute) {
      if (
        newRoute &&
        previousRoute &&
        newRoute.params &&
        previousRoute.params &&
        newRoute.params.organisationId !== previousRoute.params.organisationId
      ) {
        this.loadContacts(true);
      }
    },
    deleteContact() {
      if (this.deleteContact.delete_all) {
        this.contactsAppended = [];
        this.meta = null;
        this.page = 1;
      } else if (this.deleteContact.contact_uids.length) {
        const beforeCount = this.contactsAppended.length;
        this.contactsAppended = [...this.contactsAppended.filter(contact => !this.deleteContact.contact_uids.includes(contact.id))];

        const deletedContactsFromLoadedContacts = beforeCount - this.contactsAppended.length;

        if (deletedContactsFromLoadedContacts && deletedContactsFromLoadedContacts < this.limit && (this.meta.current_page !== this.meta.last_page)) { // TODO this has to be improved when we start working on contacts filtering and contacts improvements since this works fine only if deleted contacts are less than current page limit.
          const query = {
            limit: this.limit,
            page: this.page,
            search_query: this.searchIsNumber ? this.searchQuery.replaceAll(' ', '').replace(/^00/, '+').replace(/^0/, '') : this.searchQuery.trim(), // ignore leading 0 in case someone starts search with 09x because we store numbers as +3859xxxxxxxx
            tags_name: this.selectedTagsValues.join(),
          };
          api('GET', `/organisation/${this.organisationId}/contacts`, query, null, 'w')
            .then((response) => {
              if (response.status === 200) {
                // Load last number of contacts matching number of deleted contacts previously fo fill the gap.
                this.contactsAppended = [...this.contactsAppended, ...response.data.data.slice(response.data.data.length - deletedContactsFromLoadedContacts)];
              }
            })
            .finally(() => {
              if (this.contactsAppended.length < 50) {
                this.loadNext();
              }
            });
        }
      }
    },
    apiError(error) {
      this.errorMessage = error.data.message;
    },
    getterCtrlPanelDisplayed(isDisplayed) {
      // console.log('details panel is displayed?', isDisplayed);
      // if not displaying contact details, clear table row selected
      if (!isDisplayed) {
        this.$refs.contactsTable.clearSelected();
      }
    },
    loadingContacts() {
      if (this.contacts.meta && this.contacts.meta.current_page === 1) {
        this.meta = { ...this.contacts.meta }; // NOTE - total results in paging is acurate only on the first page so this is why we are only using it from initial response
      }
      if (!this.loadingContacts && !this.initialContactsLoaded) {
        this.initialContactsLoaded = true;
      }
      if (!this.loadingContacts && !this.isPermissionDenied) {
        this.maxContacts = this.contacts.meta.total;
        this.contacts.data.forEach((c) => {
          c.size = 0;
          this.allSelectedCheckboxModel[c.id] = true;
        });
        this.contactsAppended = [...this.contactsAppended, ...this.contacts.data];
      }
      this.forceSpinner = false;
    },
    noPermission() {
      if (this.checkPermission('can_view_all_contacts')) {
        this.loadMoreTags(true);
        this.loadContacts();
      }
    },
    selectAll() {
      ContactDetailsControl.displayPanel(false);
      this.clickedContact = null;
      this.hideTagManagerDropdown();
      if (this.selectAll) {
        // This null then 0 on next tick is needed so we do not recalculate all the changes on checkboxes initially and optimize the app to run faster
        this.preventUncheck = true;
        this.deselectedContacts = {};
        this.checkedContacts = {};
        this.$nextTick(() => { this.preventUncheck = false; });
      } else {
        this.clearSelections();
      }
      this.deselectedContacts = { ...this.deselectedContacts };
      this.tagCount = {};
      this.trackTags = {};
      this.trackTagsRemove = {};
      this.selectAllGhost = this.selectAll;
    },
    selectedRowsCounter(newValue, oldValue) {
      if (!this.selectAll && this.selectedRowsCounter === 0) {
        this.lastCheckedIndex = null;
      }
    },
    sortType() {
      if (this.sortType) {
        this.sortDirection = this.sortType.direction[0];
      }
    },
  },
  mounted() {
    if (this.$route.params.broadcast) {
      this.broadcastMode = true;
    }
    if (this.$route.params.broadcastNumberId) {
      const targetNumber = this.numbersToFilter.find(n => n.id === this.$route.params.broadcastNumberId);
      if (targetNumber) {
        this.selectedBroadcastNumber = targetNumber;
      }
    } else {
      this.selectedBroadcastNumber = this.numbersToFilter.length ? this.numbersToFilter[0] : null;
    }

    if (this.$route.query.filter) {
      api('GET', `organisation/${this.currentOrganisation.id}/contact/filter/${this.$route.query.filter}`, null, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.setFilters(response.data);
          }
        });
    }

    if (this.$route.query.s) {
      this.searchQuery = this.$route.query.s;
    }

    this.sortType = this.sortOptions[0];
    this.sortDirection = this.sortType.direction[0];
    if (this.checkPermission('can_view_all_contacts')) {
      // this.loadMoreTags(true);
      // initial list load
      this.loadMoreManagerTags();
    }
    // ensure we have contactTags
    // if (!this.contactTags.length) {
    //   this.actionFetchOrganisationContactTags({
    //     organisationId: this.organisationId,
    //     businessId: '3a9d443c-22b8-4600-bd0a-ad54759b13ba',
    //     data: null,
    //   });
    // }

    // this.refreshTags();

    // listen to changes emitted by contact details
    ContactDetailsControl.$on('change', this.updateContact);
    window.eventBus.$on('contact-updated', this.updateContactInTable);
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('blur', this.resetAllKeyStates);
    // prevent native content selection when shift is pressed
    ['keyup', 'keydown'].forEach((event) => {
      window.addEventListener(event, this.preventSelection);
    });

    this.finalizeSplashView();

    window.addEventListener('resize', this.setFilterMaxHeight);
    window.addEventListener('resize', this.contactRowsSizeReset);
    this.headerButtonsSpaceCalculator();

    window.eventBus.$on('import-finished', this.updateContactsList);
  },
  beforeDestroy() {
    ContactDetailsControl.$off('change', this.updateContact);
    window.removeEventListener('resize', this.setFilterMaxHeight);
    window.removeEventListener('resize', this.contactRowsSizeReset);
  },
  destroyed() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    ['keyup', 'keydown'].forEach((event) => {
      window.removeEventListener(event, this.preventSelection);
    });
  },
  methods: {
    ...mapActions({
      actionFetchOrganisationContacts: 'contact/fetchOrganisationContactsPage',
      actionDeleteContacts: 'contact/deleteContacts',
      fetchContact: 'contact/fetchContact',
      actionFetchOrganisationContactTags: 'contactTag/fetchOrganisationContactTags',
      finalizeSplashView: 'organisation/finalizeSplashView',
      setFilter: 'contact/setFilter',
      createContactTag: 'contactTag/createContactTag',
      fetchOrganisationNumber: 'organisation/fetchOrganisationNumber',
    }),
    ...mapMutations({
      ctrlDisplayPanelSet: 'contactDetails/CTRL_PANEL_DISPLAYED_SET',
      storeActiveSectionSet: 'contactDetails/CTRL_ACTIVE_SECTION_SET',
    }),
    getNumberIcon,
    // When contact import is finished we should reload all pages user has loaded already and add new contacts on the list without interupting any other flow
    updateContactsList(page = 1) {
      const query = {
        limit: this.limit,
        page,
        search_query: this.searchIsNumber ? this.searchQuery.replaceAll(' ', '').replace(/^00/, '+').replace(/^0/, '') : this.searchQuery.trim(), // ignore leading 0 in case someone starts search with 09x because we store numbers as +3859xxxxxxxx
      };

      if (this.sortType && this.sortType.value) {
        query.order_by = this.sortType.value;
      }

      if (this.sortDirection && this.sortDirection.value) {
        query.order_direction = this.sortDirection.value;
      }

      if (this.branchFilterCurrentItem && this.branchFilterCurrentItem.id) {
        query.business = this.branchFilterCurrentItem.id;
      }
      if (this.$route.query.filter) {
        query.filter = this.$route.query.filter;
      }

      api('GET', `/organisation/${this.organisationId}/contacts`, query, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            // console.log(response);
            let newContacts = [...this.contactsAppended, ...response.data.data];
            newContacts = this.$lodash.uniqBy(newContacts, 'id').sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
            this.contactsAppended = [...newContacts];
          }
        })
        .catch((error) => {})
        .finally(() => {
          if (page < this.page) {
            this.updateContactsList(page + 1);
          }
        });
    },
    disabledStartDates(time) {
      if (this.focusedDatePicker === null || !this.selectedFilters[this.focusedDatePicker].dateRangeEnd) {
        return false;
      }
      return time.getTime() > this.selectedFilters[this.focusedDatePicker].dateRangeEnd.getTime();
    },
    disabledEndDates(time) {
      if (this.focusedDatePicker === null || !this.selectedFilters[this.focusedDatePicker].dateRangeStart) {
        return false;
      }
      return time.getTime() < this.selectedFilters[this.focusedDatePicker].dateRangeStart.getTime();
    },
    focusAfterDelay(index) {
      this.focusedDatePicker = index;
      setTimeout(() => {
        this.datePickerOpened = true;
      }, 100);
    },
    beforeDropdownHides(event) {
      if (this.datePickerOpened) {
        event.preventDefault();
      }
    },
    addFilter() {
      if (this.selectedFilters.length < this.contactFilterLimits.maxNumberOfBlocks) {
        this.selectedFilters.push({
          type: null,
          comparison: null,
          value: null,
        });
      }
    },
    typeChange(index) {
      this.selectedFilters[index].value = null;
      delete this.selectedFilters[index].dateRangeStart;
      delete this.selectedFilters[index].dateRangeEnd;
    },
    setFilters(filter) {
      const proccessedFilters = [];
      try {
        this.operatorSaved = filter.data.query.operator;
        filter.data.query.filters.forEach((f) => {
          const setFilter = {
            type: this.filterTypes.find(type => type.value === f.type),
            comparison: this.comparisonTypes.find(compare => compare.value === f.comparator),
          };
          if (f.type === 'business') {
            setFilter.value = this.currentOrganisation.businesses.filter(number => f.ids.includes(number.id));
          } else if (f.type === 'tag') {
            setFilter.value = f.ids.map((id) => {
              return { id, name: filter.data.data.tag[id] };
            });
          } else if (f.type === 'date') {
            if (f.from) { setFilter.dateRangeStart = new Date(f.from * 1000); }
            if (f.to) { setFilter.dateRangeEnd = new Date(f.to * 1000); }
          }
          proccessedFilters.push(setFilter);
        });
      } catch {}

      this.savedFilters = [...JSON.parse(JSON.stringify(proccessedFilters))];
      // FIX date objects broken by JSON.stringify
      this.savedFilters.forEach((filter) => {
        if (filter.type.value === 'date') {
          if (filter.dateRangeStart) {
            filter.dateRangeStart = new Date(filter.dateRangeStart);
          }
          if (filter.dateRangeEnd) {
            filter.dateRangeEnd = new Date(filter.dateRangeEnd);
          }
        }
      });
      this.selectedFilters = [...proccessedFilters];
    },
    saveFilter() {
      // Remove filters that are empty
      for (let index = this.selectedFilters.length - 1; index >= 0; index--) {
        const element = this.selectedFilters[index];
        if (
          (element.type.value === 'tag' && (!element.value || !element.value.length)) ||
          (element.type.value === 'business' && (!element.value || !element.value.length)) ||
          (element.type.value === 'date' && !element.dateRangeEnd && !element.dateRangeStart)
        ) {
          this.selectedFilters.splice(index, 1);
        }
      }
      this.$refs.filterMenuDropdown.hide(true);
      this.savedFilters = [...JSON.parse(JSON.stringify(this.selectedFilters))];
      // FIX date objects broken by JSON.stringify
      this.savedFilters.forEach((filter) => {
        if (filter.type.value === 'date') {
          if (filter.dateRangeStart) {
            filter.dateRangeStart = new Date(filter.dateRangeStart);
          }
          if (filter.dateRangeEnd) {
            filter.dateRangeEnd = new Date(filter.dateRangeEnd);
          }
        }
      });
      this.operatorSaved = this.operator;
      this.filterHasChanges = !this.$lodash.isEqual(this.savedFilters, this.selectedFilters);

      const filterProcessed = this.formatFilterForbackend();

      const filterData = {
        filterData: filterProcessed,
      };
      if (this.$route.query.filter) {
        filterData.filterId = this.$route.query.filter;
      }
      this.setFilter(filterData)
        .then((response) => {
          if (response.status === 200) {
            this.selectAll = false;
            if (this.$route.query.filter !== response.data.filter_id) {
              this.$router.replace({ query: { ...this.$route.query, filter: response.data.filter_id } });
            } else if (!this.savedFilters.length) {
              this.$router.replace({ query: { ...this.$route.query, filter: undefined } });
            }
            this.forceSpinner = true;
            setTimeout(() => {
              this.loadContacts(true);
            }, 0);

            let juneFilterData = '';
            filterData.filterData.filters.forEach((f) => { juneFilterData = `${juneFilterData} ${f.type}(${f.comparator})`; });

            this.juneTrack('Contact filter applied', { filter: juneFilterData.trim(), operator: filterData.filterData.operator.toUpperCase() });
          }
        });
    },
    formatFilterForbackend() {
      const filtersData = {
        operator: this.operatorSaved,
        filters: [],
      };

      this.savedFilters.forEach((filter) => {
        const singleFilterData = {
          type: filter.type.value,
          comparator: filter.comparison.value,
        };

        if (['tag', 'business'].includes(singleFilterData.type)) {
          singleFilterData.ids = filter.value.map(f => f.id);
        } else {
          singleFilterData.from = filter.dateRangeStart ? (filter.dateRangeStart.getTime() / 1000) : null;
          singleFilterData.to = filter.dateRangeEnd ? filter.dateRangeEnd.getTime() / 1000 : null;
        }

        filtersData.filters.push(singleFilterData);
      });

      return filtersData;
    },
    cancelFilterChanges() {
      this.focusFilterBtn = false;
      this.selectedFilters = [...JSON.parse(JSON.stringify(this.savedFilters))];
      // FIX date objects broken by JSON.stringify
      this.selectedFilters.forEach((filter) => {
        if (filter.type.value === 'date') {
          if (filter.dateRangeStart) {
            filter.dateRangeStart = new Date(filter.dateRangeStart);
          }
          if (filter.dateRangeEnd) {
            filter.dateRangeEnd = new Date(filter.dateRangeEnd);
          }
        }
      });
      this.filterHasChanges = !this.$lodash.isEqual(this.savedFilters, this.selectedFilters);
      this.operator = this.operatorSaved;
    },
    cancelBroadcast() {
      this.broadcastMode = false;
    },
    removeFilterOption(index) {
      if (index === (this.selectedFilters.length - 1)) {
        const temp = this.selectedFilters.slice();
        temp.length = index;
        this.selectedFilters = [];
        this.selectedFilters = temp;
      } else {
        this.selectedFilters.splice(index, 1);
      }
    },
    clearFilter() {
      this.forceSpinner = true;
      this.selectedFilters = [];
      this.operator = 'and';
      this.saveFilter();
    },
    preventSelection() {
      document.onselectstart = () => !this.shiftActive;
    },
    closePanel(event) {
      // abort if outside click is on the table item
      const parentClasses = event ? event.target.parentNode.classList.value.split(' ') : [];
      if (
        event &&
        (
          parentClasses.includes('sb-table-item') ||
          parentClasses.includes('vue-recycle-scroller__item-view') ||
          parentClasses.includes('sb2-tag-pill-wrapper')
        )
      ) {
        return;
      }

      this.storeActiveSectionSet(null);
      this.showDetails = false;
      this.clickedContact = null;
    },
    handleKeyDown(event) {
      switch (event.keyCode) {
        case 16: // SHIFT
          this.shiftActive = true;
          break;
        case 17: // CTRL
          this.ctrlActive = true;
          break;
        case 27: // ESC
          this.clearSelections();
          this.cancelBroadcast();
          break;
        case 91: // COMMAND
          this.ctrlActive = true;
          break;
        default:
          break;
      }
    },
    handleKeyUp(event) {
      switch (event.keyCode) {
        case 16: // SHIFT
          this.shiftActive = false;
          break;
        case 17: // CTRL
          this.ctrlActive = false;
          break;
        case 91: // COMMAND
          this.ctrlActive = false;
          break;
        default:
          break;
      }
    },
    /**
     * Resets the keys that cause the checkboxes to appear on the table. Iz is triggered on blur event because if a user changes apps using cmd+tab shortcut the table would stay in the edit mode until that key is pressed again.
     */
    resetAllKeyStates() {
      this.shiftActive = false;
      this.ctrlActive = false;
      this.ctrlActive = false;
    },
    clearSelections() {
      this.trackTags = {};
      this.trackTagsRemove = {};
      this.sortedTagCount = [];
      this.selectAll = false;
      this.checkedContacts = {};
      // this.allSelected = false;
      // this.broadcastMode = false;
      Object.keys(this.allSelectedCheckboxModel).forEach((i) => { this.allSelectedCheckboxModel[i] = true; });
      this.lastCheckedIndex = null;
      this.$nextTick(() => {
        this.trackTags = {};
        this.trackTagsRemove = {};
        this.sortedTagCount = [];
        this.selectAll = false;
        this.checkedContacts = {};
        // this.allSelected = false;
        this.lastCheckedIndex = null;
      });
    },
    loadMoreTags(reset = false) {
      if ( // When resetting a contact with different business id
        reset
      ) {
        this.tagPage = 0;
        this.maxTagPages = null;
        this.availableTags = [];
      }

      this.tagPage++;
      if (this.maxTagPages !== null && this.tagPage > this.maxTagPages) {
        return;
      }

      this.tagsLoading = true;

      if (this.branchFilterCurrentItem.id) {
        this.tagApiRequest(this.branchFilterCurrentItem.id);
      } else if (this.branchesRaw && this.branchesRaw[0]) {
        this.tagApiRequest(this.branchesRaw[0].id);
      }
    },
    reSort() {
      this.$nextTick(() => {
        this.loadContacts(true);
        this.juneTrack('Contact sort by applied', { sortType: this.sortType.text, sortDirection: this.sortDirection.text });
      });
    },
    tagApiRequest(businessId) {
      const data = {
        limit: 50,
        page: this.tagPage,
        name: this.tagsSearchQuery,
        order_by: 'count_desc',
      };

      api('GET', `organisation/${this.currentOrganisation.id}/contact-tags`, data, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.availableTags = [...this.availableTags, ...response.data.data];
            this.maxTagPages = response.data.meta.last_page > this.maxTagPages ? response.data.meta.last_page : this.maxTagPages;
          }
        })
        .catch((error) => {
          this.maxTagPages = 0;
        })
        .finally(() => {
          this.tagsLoading = false;
        });
    },
    searchForTag: debounce(function f(searchValue) {
      this.tagsSearchQuery = searchValue;
      this.loadMoreTags(true);
    }, 300),
    removeContact(contactId) {
      this.meta.total = Math.max(0, this.meta.total - 1);
      this.maxContacts--;
      this.contactsAppended = this.contactsAppended.filter(contact => contact.id !== contactId);
    },
    prependContacts(contact) {
      this.meta.total += 1;
      this.maxContacts++;
      this.contactsAppended = [contact, ...this.contactsAppended];
    },
    updateContact(params) {
      // list of fields we are allowed to update
      const changeableFields = ['name', 'tags'];
      // find contact with contactId
      const record = this.contactsAppended.find(contact => contact.id === params.contactId);

      if (record) {
        changeableFields.forEach((field) => {
          if (field in params.changedData) {
            record[field] = params.changedData[field];
          }
        });
      }

      this.contactsAppended = this.contactsAppended.filter((contact) => {
        const tagNamesArray = contact.tags.map(tag => tag.name);
        let filterPass = true;
        this.selectedTagsValues.some((filterTag) => {
          if (!tagNamesArray.includes(filterTag)) {
            filterPass = false;
            return true;
          }
          return false;
        });
        return filterPass;
      });
    },
    tagSelectTag(tag) {
      // const tagIndex = this.selectedTags.indexOf(tag.id);
      // if (tagIndex > -1) {
      //   this.selectedTags.splice(tagIndex, 1);
      // } else {
      //   this.selectedTags.push(tag.id);
      // }
      // this.initialContactsLoaded = false;
      // this.page = 1;
      // this.contactsAppended = [];
      // this.loadContacts(true);

      if (this.selectedTags[tag.name]) {
        delete this.selectedTags[tag.name];
      } else {
        this.selectedTags[tag.name] = tag.id;
      }

      this.selectedTags = { ...this.selectedTags };

      this.initialContactsLoaded = false;
      this.page = 1;
      this.contactsAppended = [];
      this.loadContacts(true);
    },
    tagDeselectTags() {
      this.selectedTags = {};
      this.initialContactsLoaded = false;
      this.page = 1;
      this.contactsAppended = [];
      this.loadContacts(true);
    },
    branchFilterMenuToggle() {
      this.branchFilterMenuIsOpen = !this.branchFilterMenuIsOpen;
    },
    branchFilterMenuClose() {
      this.branchFilterMenuIsOpen = false;
    },
    branchFilterSelectItem(branch) {
      if (branch.id !== this.branchFilterCurrentItem.id) {
        this.initialContactsLoaded = false;
        this.branchFilterCurrentItem = branch;
        this.page = 1;
        this.contactsAppended = [];
        this.loadMoreTags(true);
        this.loadContacts(true);
      }
      // close menu
      this.branchFilterMenuClose();
    },
    checkIfSelected(contactId) {
      return this.selectedContacts.includes(contactId);
    },
    selectContact(contact, selected) {
      this.pageSelected = false;
      this.allSelected = false;
      if (selected) {
        this.selectedContacts = this.$lodash.union(this.selectedContacts, [contact.id]);
      } else {
        this.selectedContacts = this.$lodash.remove(this.selectedContacts, contactId => contactId !== contact.id);
      }
    },
    loadNext() {
      if (this.page < this.contacts.meta.last_page) {
        this.loadPage(this.page + 1);
      }
    },
    loadPrevious() {

    },
    markDebounce() {
      this.debounceActive = true;
      this.searchContacts();
    },
    selectAllContacts(selected) {
      if (selected) {
        this.pageSelected = true;
        this.selectedContacts = this.contacts.data.map(contact => contact.id);
      } else {
        this.selectedContacts = [];
        this.pageSelected = false;
        this.allSelected = false;
      }
    },
    searchContacts: debounce(function f() {
      this.debounceActive = false;
      this.searchQueryAfterDebounce = this.searchQuery;
      this.initialContactsLoaded = false;
      this.contactsAppended = [];
      this.clearSelection();
      this.page = 1;
      this.loadContacts();
    }, 500),
    loadPage(pageNumber) {
      if (this.loadingContacts) {
        return;
      }
      this.page = pageNumber;
      this.loadContacts();
    },
    loadContacts(resetPaginationAndSelection) {
      if (this.loadingContacts) {
        return;
      }

      if (resetPaginationAndSelection) {
        // clear selection and pagination
        this.clearSelection();
        this.contactsAppended = [];
        this.page = 1;
      }

      this.selectedContact = false;
      const query = {
        limit: this.limit,
        page: this.page,
        search_query: this.searchIsNumber ? this.searchQuery.replaceAll(' ', '').replace(/^00/, '+').replace(/^0/, '') : this.searchQuery.trim(), // ignore leading 0 in case someone starts search with 09x because we store numbers as +3859xxxxxxxx
      };

      if (this.sortType && this.sortType.value) {
        query.order_by = this.sortType.value;
      }

      if (this.sortDirection && this.sortDirection.value) {
        query.order_direction = this.sortDirection.value;
      }

      if (this.branchFilterCurrentItem && this.branchFilterCurrentItem.id) {
        query.business = this.branchFilterCurrentItem.id;
      }
      if (this.$route.query.filter) {
        query.filter = this.$route.query.filter;
      }
      this.actionFetchOrganisationContacts({
        organisationId: this.organisationId,
        data: query,
      });
    },
    selectAllPagesToggle() {
      if (this.allSelected) {
        this.clearSelection();
      } else {
        this.allSelected = true;
        this.selectedContacts = this.contacts.data.map(contact => contact.id);
        this.pageSelected = true;
      }
    },
    clearSelection() {
      this.selectedContacts = [];
      this.pageSelected = false;
      this.allSelected = false;
    },
    refreshContacts() {
      this.page = 1;
      this.loadContacts();
    },
    // contactRowClicked(record, index) {
    // 'record' will be the row data from items
    // `index` will be the visible row number (available in the v-model 'shownItems')
    contactRowClicked(record, tableIndex) {
      // load contact details
      ContactDetailsControl.loadContactDetails(this.organisationId, record.id);
      if ((!this.getterCtrlPanelDisplayed) || (this.getterCtrlPanelDisplayed && this.clickedContact && this.clickedContact.id !== record.id)) {
        ContactDetailsControl.displayPanel(true);
        this.selectTableRow(tableIndex);
        this.clickedContact = record;
      } else {
        ContactDetailsControl.displayPanel(false);
        this.clickedContact = null;
      }
    },
    showContactDetails(contactData, tableIndex) {
      // load contact details
      ContactDetailsControl.loadContactDetails(this.organisationId, contactData.id);
      if ((!this.getterCtrlPanelDisplayed && !this.clickedContact) || (this.getterCtrlPanelDisplayed && this.clickedContact && this.clickedContact.id !== contactData.id)) {
        ContactDetailsControl.displayPanel(true);
        this.selectTableRow(tableIndex);
        this.clickedContact = contactData;
      } else {
        ContactDetailsControl.displayPanel(false);
        this.clickedContact = null;
      }
    },
    confirmDeleteSingleContact() {
      if (!this.canDeleteContact) {
        this.showToaster('You don\'t have a permission to do this action.', null, null, 'warning');
        return;
      }

      // since we are deleting just in case one item is selected then we have to find it and filter it out first
      Object
        .entries(this.checkedContacts)
        .filter(c => c[1])
        .forEach((c) => {
          const contact = this.contactsAppended.find(ca => ca.id === c[0]);
          this.$confirmPluginModal.show({
            buttonVariant: 'RED',
            title: 'Delete contact',
            message: contact.name ? `Are you sure you want to delete <b>${contact.name}</b> ${this.$options.filters.phoneFormat(contact.phone)}?` : `Are you sure you want to delete <b>${this.$options.filters.phoneFormat(contact.phone)}</b>?`,
            confirmButtonText: 'Delete',
            showUndoWarning: true,
            autoClose: false,
            confirmCallback: () => {
              this.deleteSingleContact(contact);
              this.$nextTick(() => {
                this.clearSelections();
              });
            },
          });
        });
    },
    deleteSingleContact(contactData) {
      this.actionDeleteContacts({
        organisationId: this.organisationId,
        data: {
          contact_ids: [contactData.id],
        },
      }).then(
        (success) => {
          this.removeContact(contactData.id);
          this.showToaster('Contact has been successfully deleted.', null, null, 'OK');
          this.$confirmPluginModal.close();
        },
        (error) => {
          this.$confirmPluginModal.finishWorking();
          this.showToaster('Error while deleting contact. Please try again.', null, null, 'error');
        },
      );
    },
    openCreateContactModal() {
      // TODO fix the permission here
      if (this.canAddContact) {
        this.$refs.createContactModal.show();
      } else {
        this.showToaster('You don’t have a permission to do this action.', null, null, 'warning');
      }
    },
    onContactCreateSuccess(contactsList) {
      // console.log('onContactCreateSuccess', contactsList);
      // prepend new contacts into current contacts list
      this.$refs.createContactModal.hide();
      this.prependContacts(contactsList);
      this.allSelectedCheckboxModel[contactsList.id] = true;
      this.allSelectedCheckboxModel = { ...this.allSelectedCheckboxModel };
      // const message = contactsList.length > 1 ? 'Contacts have been successfully created.' : 'Contact has been successfully created.';
      this.showToaster('Contact has been successfully created.', null, null, 'OK');
    },
    onContactCreateError(errorMessage) {
      // console.error(response);
      this.showToaster(errorMessage, null, null, 'error');
    },
    selectTableRow(tableIndex) {
      // no need to unselect since table select-mode is "single"
      this.$refs.contactsTable.selectRow(tableIndex);
    },
    refreshTags() {
      this.actionFetchOrganisationContactTags({
        organisationId: this.organisationId,
        businessId: this.branchFilterCurrentItem.id,
      });
    },
    _testToast() {
      this.showToaster('Contact has been successfully created.', null, null, 'OK');
    },
    updateContactInTable(data) {
      const targetContactIndex = this.contactsAppended.findIndex(contact => contact.id === data.id);
      if (targetContactIndex >= 0) {
        this.contactsAppended[targetContactIndex] = data;
        this.contactsAppended = [...this.contactsAppended];
      }
    },
    selectRow(event, contact, index, force = false) {
      if (force || event.shiftKey || event.ctrlKey || event.metaKey) { // metaKey is commandKey ona Mac
        if (this.selectAll) {
          this.$refs[`all-checkbox-${contact.id}`].$el.children[1].click();
        } else {
          this.$refs[`checkbox-${contact.id}`].$el.children[1].click();
        }
      }

      if (!force && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        if (!this.showDetails || (this.showDetails && this.clickedContact && this.clickedContact.id !== contact.id)) {
          ContactDetailsControl.loadContactDetails(this.organisationId, contact.id);
          if (this.activeSection) {
            this.storeActiveSectionSet(this.activeSection);
          } else {
            this.storeActiveSectionSet('info');
          }
          this.showDetails = true;
          this.clickedContact = contact;
        } else {
          this.closePanel();
        }
        this.lastCheckedIndex = null;
      }
    },
    allCheckboxChange(state, contactId, index) {
      if (this.preventUncheck) {
        return;
      }
      if (state) {
        delete this.deselectedContacts[contactId];
      } else {
        this.deselectedContacts[contactId] = true; // 'HERE'
      }
      this.deselectedContacts = { ...this.deselectedContacts };

      this.$nextTick(() => {
        if (this.selectAllGhost && this.deselectedContacts[contactId]) {
          this.trackTagsRemove[contactId] = this.contactsAppended.find(c => c.id === contactId).tags.map(t => ({ id: t.id, name: t.name }));
        } else if (this.selectAllGhost && !this.deselectedContacts[contactId]) {
          delete this.trackTagsRemove[contactId];
        }

        if (this.shiftActive && this.lastCheckedIndex !== null && this.lastCheckedIndex >= 0) {
          const from = Math.min(this.lastCheckedIndex, index);
          const to = Math.max(this.lastCheckedIndex, index);

          for (let i = from; i <= to; i++) {
            this.allSelectedCheckboxModel[this.contactsAppended[i].id] = state;
            if (state) {
              delete this.trackTagsRemove[this.contactsAppended[i].id];
            } else {
              this.trackTagsRemove[this.contactsAppended[i].id] = this.contactsAppended.find(c => c.id === this.contactsAppended[i].id).tags.map(t => ({ id: t.id, name: t.name }));
            }
          }

          this.allSelectedCheckboxModel = { ...this.allSelectedCheckboxModel };
        }

        this.lastCheckedIndex = index;
        this.negativeRecountTags();
      });
    },
    contactRowsSizeReset() {
      this.contactsAppended.forEach((c) => {
        c.recalc = true;
      });
      this.contactsAppended = [...this.contactsAppended];
    },
    recycleScrollerUpdate: debounce(function f(startIndex, endIndex) {
      let updated = 0;

      setTimeout(() => {
        for (let index = startIndex; index <= endIndex; index++) {
          if (this.$refs[`recycle-scroller-${index}`] && this.contactsAppended[index] && (!this.contactsAppended[index].size || this.contactsAppended[index].recalc)) {
            this.contactsAppended[index].size = this.$refs[`recycle-scroller-${index}`].offsetHeight + 4;
            updated++;
            this.contactsAppended[index].recalc = false;
          }
        }

        if (updated) {
          // Reinitialize list so the components reacts to the new sizes
          this.contactsAppended = [...this.contactsAppended];
        }
      }, 0);
    }, 0),
    removeHover(index) {
      this.showCheckboxForIndex = null;
      const cells = this.$refs['table-area'].getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.remove('hover-grid');
      }
    },
    addHover(index) {
      const cells = this.$refs.tableGrid['table-area'].getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.add('hover-grid');
      }
    },
    showCheckbox(index) {
      this.addHover(index);
      this.showCheckboxForIndex = index;
    },
    setFilterMaxHeight() {
      const position = this.$refs.filterMenuDropdown.$el.children[1].getBoundingClientRect();
      const maxHeightForFilter = window.innerHeight - position.top;
      this.$refs.filterMenuDropdown.$el.children[1].style.maxHeight = `${maxHeightForFilter}px`;
      this.showScrollbarOnFilter = this.$refs.filterMenuDropdown.$el.children[1].scrollHeight > Number(this.$refs.filterMenuDropdown.$el.children[1].style.maxHeight.replace('px', ''));

      // Also calculate the available space for header buttons
      this.headerButtonsSpaceCalculator();
    },
    headerButtonsSpaceCalculator() {
      const buttonBreakPoint = 700;
      clearTimeout(this.spaceCalculatorTimeout);
      const tableToolsRect = this.$refs.tableTools.getBoundingClientRect();
      this.spaceCalculatorTimeout = setTimeout(() => {
        const e1Rect = this.$refs.leftToolsRef.getBoundingClientRect();
        this.headerSpaceAvailable = tableToolsRect.width - e1Rect.width;
        if (this.$refs.rightToolsActionsRef) {
          this.showActionDropdown = !(this.headerSpaceAvailable > buttonBreakPoint);
        } else {
          this.showActionDropdown = this.headerSpaceAvailable <= buttonBreakPoint;
        }
      }, 10);
    },
    // This method partially duplicates tagApiRequest method which is because there is no vuex oriented tags arhitecture which needs to be solved and redesigned in v3.
    // The idea should be to load first tag page on page load and then load additional tags via paging as needed but first page should always be available to ease the load on the backend and optimize frontend speed.
    loadManagerTags() {
      const data = {
        limit: 50,
        page: this.tagManagerPage,
        name: this.tagsManagerSearchQuery,
        order_by: 'count_desc',
      };

      api('GET', `organisation/${this.currentOrganisation.id}/contact-tags`, data, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.availableManagerTags = [...this.availableManagerTags, ...response.data.data];
            this.maxManagerTagPages = response.data.meta.last_page > this.maxManagerTagPages ? response.data.meta.last_page : this.maxManagerTagPages;
            if (this.selectAllGhost) {
              this.tagsCalculatorForSelectAllActive();
            }
          }
        })
        .catch((error) => {
          this.maxManagerTagPages = 0;
        })
        .finally(() => {
          this.tagsManagerLoading = false;
        });
    },
    searchTags: debounce(function f(searchValue) {
      if (
        this.forceTagReload ||
        (this.availableManagerTags.length) ||
        (!this.availableManagerTags.length && (this.tagsManagerSearchQuery.length < this.lastTagsManagerSearchQuery.length))
      ) {
        this.loadMoreManagerTags(true, true);
        this.forceTagReload = false;
      }
    }, 3),
    loadMoreManagerTags(bottom, reset = false) {
      if (this.tagsManagerLoading && !reset) {
        return;
      }
      this.lastTagsManagerSearchQuery = this.tagsManagerSearchQuery;
      if ( // When resetting a contact with different business id
        reset
      ) {
        this.tagManagerPage = 0;
        this.maxManagerTagPages = null;
        this.availableManagerTags = [];
      }

      this.tagManagerPage = this.tagManagerPage + 1;
      if (this.maxManagerTagPages !== null && this.tagManagerPage > this.maxManagerTagPages) {
        return;
      }

      this.tagsManagerLoading = true;
      this.loadManagerTags();
    },
    checkUnchecForContact(state, contactId, index) {
      setTimeout(() => {
        if (!this.selectAllGhost && this.checkedContacts[contactId]) {
          this.trackTags[contactId] = this.contactsAppended.find(c => c.id === contactId).tags.map(t => ({ id: t.id, name: t.name }));
        } else if (!this.selectAllGhost && !this.checkedContacts[contactId]) {
          delete this.trackTags[contactId];
        }

        if (this.shiftActive && this.lastCheckedIndex !== null && this.lastCheckedIndex >= 0) {
          const from = Math.min(this.lastCheckedIndex, index);
          const to = Math.max(this.lastCheckedIndex, index);

          for (let i = from; i < to; i++) {
            this.checkedContacts[this.contactsAppended[i].id] = state;
            if (state) {
              this.trackTags[this.contactsAppended[i].id] = this.contactsAppended.find(c => c.id === this.contactsAppended[i].id).tags.map(t => ({ id: t.id, name: t.name }));
            } else {
              delete this.trackTags[this.contactsAppended[i].id];
            }
          }

          this.checkedContacts = { ...this.checkedContacts };
        }

        // unselect select page checkbox when some contact is unselected
        if (!state) {
          this.selectPage = false;
        }

        this.lastCheckedIndex = index;
        this.recountTags();
      });
    },
    recountTags(state) {
      if (this.preventRecount) {
        return;
      }
      this.tagCount = {};
      this.tagsCountCheckbox = {};
      Object.values(this.trackTags).forEach((tags) => {
        tags.forEach((tag) => {
          if (!this.tagCount[tag.id]) {
            this.tagCount[tag.id] = {
              count: 1,
              id: tag.id,
              name: tag.name,
            };
            this.tagsCountCheckbox[tag.id] = true;
          } else {
            this.tagCount[tag.id].count++;
          }
        });
      });
      this.tagsCountCheckbox = { ...this.tagsCountCheckbox };
      // this.tagsCountCheckboxEdit = JSON.parse(JSON.stringify(this.tagsCountCheckbox));
      this.sortedTagCount = Object.values(this.tagCount).sort((a, b) => b.count - a.count);
    },
    negativeRecountTags() {
      this.negativeTagCount = {};
      Object.values(this.trackTagsRemove).forEach((tags) => {
        tags.forEach((tag) => {
          if (!this.negativeTagCount[tag.id]) {
            this.negativeTagCount[tag.id] = 1;
          } else {
            this.negativeTagCount[tag.id]++;
          }
        });
      });
      this.negativeTagCount = { ...this.negativeTagCount };
    },
    preloadTags() {
      if (!this.availableManagerTags.length) {
        this.searchTags();
      }
    },
    setPartialToAll(state, isPartial, tag) {
      this.editedTagCheckbox[tag.id] = true;
      this.editedTagCheckbox = { ...this.editedTagCheckbox };
      this.tagsBackup[tag.id] = tag;

      const targetTag = this.sortedTagCountEdit.find(t => t.id === tag.id);
      if (!targetTag.initialCount && targetTag.initialCount !== 0) {
        targetTag.initialCount = targetTag.count < this.selectedContactsCounter ? targetTag.count : this.selectedContactsCounter;
      }

      this.$nextTick(() => {
        this.partialEdited[tag.id] = true;
        this.tagsCountCheckboxEdit[tag.id] = true;
        if (targetTag.count && targetTag.count < this.selectedContactsCounter) { // partial to full
          targetTag.count = this.selectedContactsCounter;
        } else if (targetTag.count && targetTag.count === this.selectedContactsCounter) { // full to off
          targetTag.count = 0;
          this.tagsCountCheckboxEdit[tag.id] = false;
        } else { // off to partial or full
          targetTag.count = targetTag.initialCount || this.selectedContactsCounter;
        }

        if (targetTag.count === targetTag.initialCount) { // remove from comparison
          delete this.editedTagCheckbox[tag.id];
          // delete this.partialEdited[tag.id];
        }

        this.partialEdited = { ...this.partialEdited };
        this.tagsCountCheckboxEdit = { ...this.tagsCountCheckboxEdit };
      });
    },
    editTagCheckbox(state, isPartial, tag) {
      this.editedTagCheckbox[tag.id] = true; // This can be set to state and then evaluated on save to only save what is changed
      this.editedTagCheckbox = { ...this.editedTagCheckbox };
      this.tagsBackup[tag.id] = tag;
      const targetTag = this.availableManagerTags.find(t => t.id === tag.id);
      if (!targetTag.initialCount && targetTag.initialCount !== 0) {
        targetTag.initialCount = targetTag.count < this.selectedContactsCounter ? targetTag.count : this.selectedContactsCounter;
      }

      this.$nextTick(() => {
        this.partialEdited[tag.id] = true;
        if (targetTag.count && targetTag.count === this.selectedContactsCounter) { // full to off
          targetTag.count = 0;
          this.tagsCountCheckboxEdit[tag.id] = false;
        } else if (!targetTag.count) { // off to partial or full
          targetTag.count = targetTag.initialCount || this.selectedContactsCounter;
          this.tagsCountCheckboxEdit[tag.id] = true;
        } else { // partial to full
          targetTag.count = this.selectedContactsCounter;
          this.tagsCountCheckboxEdit[tag.id] = true;
        }

        this.partialEdited = { ...this.partialEdited };
        this.tagsCountCheckboxEdit = { ...this.tagsCountCheckboxEdit };
        this.editedTagCheckbox = { ...this.editedTagCheckbox };
      });
    },
    showTagManagerDropdown() {
      if (this.selectAllGhost) {
        this.tagsCalculatorForSelectAllActive();
      }
      this.focusManageTagsBtn = true;
      if (this.selectAllGhost) {
        this.tagsCountCheckboxEdit = JSON.parse(JSON.stringify(this.tagsCountCheckbox));
        this.sortedTagCountEdit = [];
      } else {
        this.tagsCountCheckboxEdit = JSON.parse(JSON.stringify(this.tagsCountCheckbox));
        this.sortedTagCountEdit = JSON.parse(JSON.stringify(this.sortedTagCount));
      }
    },
    hideTagManagerDropdown() {
      this.focusManageTagsBtn = false;
      this.editedTagCheckbox = {};
      this.tagsCountCheckboxEdit = {};
      // this.tagsCountCheckbox = {};
      this.partialEdited = {};
    },
    tagsCalculatorForSelectAllActive() {
      const tagsCountCheckboxTemp = {};
      this.availableManagerTags.forEach((at) => {
        tagsCountCheckboxTemp[at.id] = !!(at.count - (Number(this.negativeTagCount[at.id]) || 0));
      });

      this.tagsCountCheckbox = { ...tagsCountCheckboxTemp };
      this.tagsCountCheckboxEdit = { ...JSON.parse(JSON.stringify(this.tagsCountCheckbox)), ...this.tagsCountCheckboxEdit };
    },
    selectContactsOnPage(state) {
      this.preventRecount = true;

      this.contactsAppended.forEach((contact) => {
        this.checkedContacts[contact.id] = state;
      });
      this.checkedContacts = { ...this.checkedContacts };

      this.selectPage = state;

      this.$nextTick(() => {
        this.preventRecount = false;
        this.recountTags();
      });
    },
    createTag() {
      const data = {
        name: this.tagsManagerSearchQuery,
      };
      this.tagsManagerLoading = true;
      this.forceTagReload = true;
      this.createContactTag({
        organisationId: this.organisationId,
        // businessId: this.businessId,
        data,
      })
        .then((response) => {
          // this.initialEditName = response.name;
          this.showToaster('Tag has been successfully created.', null, null, 'OK');
          this.searchTags();
        })
        .catch(() => {
          this.tagsManagerLoading = false;
        })
        .finally(() => {
        });
    },
    forceTagManagerClose(hideCallback = () => {}) {
      this.sortedTagCountEdit = [];
      this.availableManagerTags = [];
      hideCallback();
      this.tagsManagerSearchQuery = '';
      this.forceTagReload = true;
    },
    saveTagChangesPrecheck(hideCallback = () => {}) {
      if (this.selectAllGhost && !this.$route.query.filter) {
        this.setFilter({ filterData: { operator: 'and', filters: [] } })
          .then((response) => {
            if (response.status === 200) {
              this.saveTagChanges(hideCallback, response.data.filter_id);
              // this.juneTrack('Contacts tags manager save', {});
            }
          })
          .catch(() => {
            this.showToaster('Something went wrong. Please try again.', null, null, 'error');
          });
      } else {
        this.saveTagChanges(hideCallback);
      }
    },
    saveTagChanges(hideCallback = () => {}, filterId = '') {
      const editedTagsIDs = Object.keys(this.editedTagCheckbox);
      const data = {
        tags_add: editedTagsIDs.filter(tagId => this.tagsCountCheckboxEdit[tagId]),
        tags_remove: editedTagsIDs.filter(tagId => !this.tagsCountCheckboxEdit[tagId]),
      };

      if (this.selectAllGhost) {
        data.filter_uid = this.$route.query.filter || filterId;
        data.deselected_contacts_ids = Object.keys(this.deselectedContacts);
        delete data.contact_uids;
      } else {
        data.contact_uids = Object.keys(this.checkedContacts).filter(checkedContactId => this.checkedContacts[checkedContactId]);
        delete data.filter_uid;
        delete data.deselected_contacts_ids;
      }

      // Save data until request resolves
      this.dataBackup = {};

      // Update local contacts with updates
      this.contactsAppended.forEach((contact) => {
        if ((this.selectAllGhost && !Object.keys(this.deselectedContacts).includes(contact.id)) || (data.contact_uids && data.contact_uids.includes(contact.id))) {
          this.dataBackup[contact.id] = {
            tags_added: [],
            tags_removed: [],
          };
          // Adding new tags
          data.tags_add.forEach((tagId) => {
            const targetTag = this.tagsBackup[tagId];
            if (targetTag && !contact.tags.find(t => t.id === targetTag.id)) {
              contact.tags.push(targetTag);
              this.dataBackup[contact.id].tags_added.push(tagId);
            }
          });
          // removing tags
          data.tags_remove.forEach((removeTagId) => {
            contact.tags = contact.tags.filter(tag => tag.id !== removeTagId);
            this.dataBackup[contact.id].tags_removed.push(removeTagId);
          });
        }
      });

      // Run request
      const request = api('POST', `/organisation/${this.organisationId}/contacts/contact-tags`, data, null, 'w');
      request
        .then((response) => {
          if (response.status === 200) {
            // On success clear saved data
            this.dataBackup = {};
            this.showToaster('Tags have been successfully updated.', null, null, 'OK');
          }
        })
        .catch(() => {
          // On fail reset local contacts changes
          this.showToaster('Something went wrong. Please try again.', null, null, 'error');
          const affectedContactsIDs = Object.keys(this.dataBackup);
          this.contactsAppended.forEach((contact) => {
            if (affectedContactsIDs.includes(contact.id)) {
              // Adding old tags
              this.dataBackup[contact.id].tags_removed.forEach((tagId) => {
                const targetTag = this.tagsBackup[tagId];
                if (targetTag && !contact.tags.find(t => t.id === targetTag.id)) {
                  contact.tags.push(targetTag);
                }
              });
              // removing new tags
              this.dataBackup[contact.id].tags_added.forEach((removeTagId) => {
                contact.tags = contact.tags.filter(tag => tag.id !== removeTagId);
              });
            }
          });
        })
        .finally(() => {
          if (this.contactsAppended.length < 50) {
            this.loadNext();
          }
        });

      hideCallback();
      this.$nextTick(() => {
        this.clearSelections();
      });
    },
    startBranchSelect() {
      this.filterBranchPopoverVisible = true;
      if (this.$refs.newMessagePopover) {
        this.$refs.newMessagePopover.$emit('disable');
      }
    },
    endBranchSelect() {
      this.filterBranchPopoverVisible = false;
    },
    closePopover() {
      this.$refs.branchFilterPopover.$emit('close');
    },
    startBroadcastMode() {
      this.updateNumberInfo();
      this.juneTrack('Broadcast start', { from: 'Contacts page' });
      this.broadcastMode = true;
      if (this.$refs.rightToolsActionsRef) {
        this.$refs.rightToolsActionsRef.hide();
      }
    },
    broadcastBranchSelected(number) {
      this.closePopover();
      this.selectedBroadcastNumber = number;
      this.updateNumberInfo();
    },
    showBroadcastModal() {
      if (this.selectAll) {
        this.$refs.broadcastModalRef.show(this.selectedBroadcastNumber, null, true, this.$route.query.filter, this.$route.query.s);
      } else {
        this.$refs.broadcastModalRef.show(this.selectedBroadcastNumber, Object.keys(this.checkedContacts).filter(cc => this.checkedContacts[cc]));
      }
    },
    createNewTemplate() {
      const config = {
        backAction: () => {
          this.$refs.messageTemplateModalRef.hide();
        },
        noFade: true,
        forceNew: true,
      };
      this.createNewModalVisible = true;
      this.$refs.messageTemplateModalRef.show(config);
    },
    closeModals() {
      this.$refs.broadcastModalRef.hide();
    },
    updateNumberInfo() {
      this.fetchOrganisationNumber(this.selectedBroadcastNumber.id)
        .then(() => {
          setTimeout(() => {
            const targetBranch = this.currentOrganisation.businesses.find(b => b.id === this.selectedBroadcastNumber.id);
            this.selectedBroadcastNumber = targetBranch;
          }, 0);
        });
    },
    forceSelectAll(e) {
      if (e) {
        this.selectAll = true;
      }
    },
    restateSelection(e) {
      if (!e && ((this.deselectedRowsCounter && this.selectAll) || (this.selectedRowsCounter && this.maxContacts !== this.selectedRowsCounter))) {
        this.selectAll = true;
        this.deselectedContacts = {};
        this.selectAllUnselected();
      } else if (!e && this.selectedRowsCounter && this.maxContacts !== this.selectedRowsCounter) {
        // this.selectAll = true;
      } else if (!e) {
        this.selectAll = false;
      } else if (e && this.maxContacts !== this.selectedRowsCounter) {
        this.selectAll = true;
        this.deselectedContacts = {};
        this.selectAllUnselected();
      }

      setTimeout(() => {
        if (this.$refs.selectAllRef && !this.$refs.selectAllRef.$el.children[0].checked) {
          this.$refs.selectAllRef.$el.children[0].click();
        }
      }, 0);
    },
    selectAllUnselected() {
      Object.keys(this.allSelectedCheckboxModel).forEach((k) => { this.allSelectedCheckboxModel[k] = true; });
    },
    resetBroadcast() {
      this.cancelBroadcast();
      this.clearSelections();
      setTimeout(() => {
        if (this.$refs.ForceAllRef && this.$refs.ForceAllRef.$el && this.$refs.ForceAllRef.$el.children && this.$refs.ForceAllRef.$el.children[0] && this.$refs.ForceAllRef.$el.children[0].checked) {
          this.$refs.ForceAllRef.$el.children[0].click();
        }
      }, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
.contacts-table-dashboard {
  overflow: auto;
  font-size: 14px;
  color: #141c20;
  // overflow: auto cuts-off dropdowns (branch selection for example)
  // overflow: auto;

  .scroll-area {
    padding: 0 40px;

    &.v-scroll-visible {
      margin-right: 3px;
    }
  }

  .scroll-area.tag-manager-scroll-area {
    padding: 0;
    margin-right: 0;
    max-height: 400px;
    padding-bottom: 7px;

    .tag p.name {
      line-height: normal;
    }
  }

  // title
  .dashboard_title {
    font-size: 22px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 0.87;
    letter-spacing: normal;
    text-align: left;
    color: #141c20;

    margin-top: 35px;
    margin-bottom: 0;
  }

  // utilities

  // override input placeholder color
  input::placeholder {
    color: $greyPrimary !important;
  }

  // muted text
  .muted {
    color: #9eafb5;
  }

  // branch filter
  .branch-filter-wrapper {
    display: flex;
    align-items: center;
    justify-content: left;
    margin-top: 10px;
    position: relative;

    .branch-filter-info {
      display: flex;
      align-items: center;
      justify-content: left;
      cursor: pointer;

      .branch-icon-wrap {
        width: 16px;
        font-size: 10px;
        // padding-bottom: 2px; // font icon refuses to center vertically - but it messes up regular icons
      }
      .branch-name {
        color: #7c8b91;
        font-size: 14px;
        font-weight: 500;
      }

      .down-arrow {
        background-image: url('~@/assets/img/SB-Icons/down-arrow-light.svg');
        width: 12px;
        height: 7px;
        margin-left: 6px;
        background-repeat: no-repeat;
        background-size: 12px;
        background-position: center;
      }
    }
    // menu open and hover
    .branch-filter-info:hover, .branch-filter-info.menu-open {
      .down-arrow
      {
        background-image: url('~@/assets/img/SB-Icons/down-arrow-dark.svg');
      }
      // branch name on hover
      // black on hover
      .branch-name {
        color: $blackPrimary;
      }
    }

    // branch selection menu
    .sb2-dropdown {
      position: absolute;
      left: 0;
      top: 21px;
      z-index: 1100;
    }
  }

  .table-tools {
    margin-top: 15px;
    background: $white;
    padding: 20px 0;
    border-bottom: solid 1px #e6ebed;
  }

  .sb2-bordered {
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px #cfd8dc;
    border-radius: 5px;

    &.sb2-hoverable:hover {
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
    }

    &.sb2-focusable:focus {
      outline: none !important;
      border: solid 1px #cfd8dc;
      box-shadow: 0 0 0 3px #bfdeff !important;
    }
  }

  // tags - filter button
  // could become standard button
  .filter-button {
    display: inline-flex;
    position: relative;
    box-sizing: border-box;
    height: 30px;

    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px #cfd8dc;
    border-radius: 5px;
    background-color: $white;

    align-items: center;
    padding: 0;
    margin-right: 10px;
  }
  .filter-button-main-content {
    padding: 0 16px;
    display: flex;
    align-items: center;
    font-weight: 500;
    .sb2-icon {
      margin-right: 6px;
    }

    .sort-prefix {
      font-size: 14px;
      color: #54626c;
    }

    .sort-type {
      font-size: 14px !important;
      font-weight: 500 !important;
      color: $blackPrimary !important;
    }
  }
  .filter-button-filter-count {
    height: 22px;
    border-left: 1px solid #cfd8dc;
    padding: 0 8px;
    display: flex;
    align-items: center;

    font-weight: 500;
    color: #2680eb;
  }

  // tags - associated dropdown
  .sb2-wrapper {
    position: relative;
    display: inline-flex;
    height: 30px;
    column-gap: 10px;
  }
  .sb2-dropdown {
    position: absolute;
    top: 41px;
    left: 1px;
    z-index: 100;
    width: 297px;
    background-color: $white;
    border-radius: 5px;
    box-shadow: 0 2px 5px 0 rgba(13, 15, 22, 0.23)
  }

  // tag pills
  .sb2-tag-pill {
    color: #2680eb;
    font-size: 12px;
    font-weight: 500;
    background-color: #e7f1fe;
    border-radius: 10px;
    padding: 0 8px;
    height: 20px;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;

    // spacing
    margin: 6px 5px;

    // info when contact is unsubscribed
    &.status-unsubscribed {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
      background-color: #fbe4e5;
      color: #ff5b61;
      font-size: 12px;
      height: 20px;
      border-radius: 11px;
      padding-left: 9px;

      div.sb-icon {
        margin-left: 6px;
        margin-right: 4px;
      }
    }

    &.no-edit {
      background-color: #edf0f2;
      color: $blackPrimary;
    }
  }
  // fix spacing for tag pills
  .sb2-tag-pill-wrapper {
    margin: 10px 0px !important;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    .sb2-tag-pill {
      margin: 3px 3px !important;
    }
  }

  .table-wrapper {
    margin-top: 0;
    margin-bottom: 20px;
  }

  // fix checkboxes and labels
  .custom-control {
    margin: 0 !important;
    padding: 0;
    left: auto;
    align-items: center;
    // height is set to 24px for some reason...
    height: 16px;
    min-height: 16px;
    min-width: 16px;
  }
  .custom-control label.custom-control-label {
    margin: 0;
    height: 14px;

    &:before, &:after {
      height: 20px;
      width: 20px;
      // fix silly positioning
      top: 0;
      left: 0;
    }

    &:before {
      // visible checkbox
      border-radius: 3px;
      box-shadow: 0 1px 2px 0 rgba(48, 54, 70, 0.15);
      border: solid 1px #cfd8dc;
      background-color: #ffffff;
    }
  }
  // when unchecked
  .custom-control.custom-checkbox .custom-control-input:not(:checked)~.custom-control-label {
    &:before {
      // visible checkbox
      border-radius: 3px;
      box-shadow: 0 1px 2px 0 rgba(48, 54, 70, 0.15);
      border: solid 1px #cfd8dc;
      background-color: #ffffff;
    }
  }
  // when checked
  .custom-control.custom-radio .custom-control-input:checked~.custom-control-label,
  .custom-control.custom-checkbox .custom-control-input:checked~.custom-control-label {
    &:before {
      background-color: #141c20;
      // remove gray border (box-sizing is border-box, we can just remove border)
      border: none;
    }
    &:after {
      // icon on top
      color: $white;
      // fix opacity
      opacity: 1 !important;
    }
  }

  // other
  .tag-menu-item {
    .dropdown-item {
      position: relative;
      padding: 10px 20px 10px 15px!important;
    }

    .marked-tag {
      position: absolute;
      right: 5px;
      top: 7px;
    }
  }

  .no-contacts-wrapper {
    margin: 35px auto;
    width: 100%;
    max-width: 782px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;

    border: 1px solid #e1e5eb;
    border-radius: 15px;
    background: linear-gradient(to bottom, $white, $greyUltralight);

    .sb2-icon {
      margin: 32px auto 16px auto;
    }
    h5 {
      color: $blackPrimary;
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 10px;
    }
    .description {
      color: $blackUltralight;
      font-size: 14px;
      margin-bottom: 40px;
    }
    .options {
      display: flex;
      justify-content: center;
      margin-bottom: 35px;

      button, .import {
        width: fit-content;
        margin: 0 auto;
      }

      .import {
        padding: 3px 6px;
        color: #54626C;

        * {
          font-weight: 500;
          font-size: 14px;
        }

        &:hover {
          cursor: pointer;
          * {
            color: $blackPrimary;
          }
          ::v-deep .sb-icon .mask {
            background: $blackPrimary !important;
          }
        }
      }
    }
  }
}

.sticky-top {
  background-color: $white;

  // For the seccond row of sticky tops since sometimes we can't put all of the sticky elements inside one wrapper.
  &.sticky-second {
    top: 71px;
  }
}

.empty-results {
  margin: 0 auto;
  background-color: #f2f5f7;
  font-size: 14px;
  width: fit-content;
  margin-top: 40px;
  margin-bottom: 40px;
  padding: 10px 18px 10px 12px;
  border-radius: 50px;
  cursor: default;
  text-align: center;
  color: $blackLight;

  .emoji {
    font-size: 16px;
    margin-right: 8px;
    font-family: 'apple color emoji,android emoji,emojione mozilla,twemoji mozilla,segoe ui emoji,noto color emoji,emojisymbols,segoe ui symbol';
  }

  p {
    font-size: 14px;
  }

  strong {
    font-weight: 500;
  }
}

.more-icon {
  margin-right: 14px;
}

.no-data {
  border-top: 0px;
}

.spinner-padding {
  border-top: 0px;
  padding-top: 34px;
}

.table__grid {
  z-index: 1019;
  position: relative;
  grid-template-columns: minmax(50px, min-content) minmax(50px, min-content) minmax(auto, 100%) max-content !important;

  .column-name {
    border-top: 0px;
  }

  .selected-counter {
    font-size: 14px;
    font-weight: bold;
    color: $bluePrimary;
    .suffix {
      font-size: 14px;
      font-weight: normal;
      color: $blackUltralight;
    }
  }

  .table-header {
    height: 51px;
    align-items: center;
    display: flex;
  }

  .custom-checkbox {
    margin-left: 0px !important;
  }

  .full-width {
    grid-column: 1 / 5 !important;
  }

  .first {
    min-height: 48px !important;
  }

  .name-column {
    padding-left: 8px;
  }

  .more-column {
    padding-right: 14px;
    span {
      color: #54626C;
    }
  }

  .checkbox-wrapper {
    height: 24px;
    width: 24px;
    margin-right: 8px;
    display: block;
    overflow: hidden;
    position: relative;
    cursor: pointer;

    // Overlay is needed because clicking directly on the checbox doesn't set the last selected index which messes up the shift + click selection
    &.overlay-checkbox::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
    }

    ::v-deep .custom-control {
      &:hover {
        background: unset;
      }

      .custom-control-label {
        margin: 0;
        height: 15px;
        width: 18px;

        &:before, &:after {
          height: 15px !important;
          width: 15px !important;
          // fix silly positioning
          top: 0 !important;
          left: 0 !important;
        }

        &:after {
          color: $white;
          opacity: 1 !important;
        }
      }
    }
  }

  .hide-on-hover {
    display: inherit;
  }

  .show-on-hover {
    display: none;
  }

  .hover-grid {
    .hide-on-hover {
      display: none;
    }

    .show-on-hover {
      display: inherit;
    }
  }
}

.sticky-top {
  .header-checkbox {
    margin-left: 5px !important;
    margin-right: 9px !important;
  }

  ::v-deep {
    .custom-control-label {
      margin: 0;
      height: 15px;
      width: 18px;

      &:before, &:after {
        height: 15px !important;
        width: 15px !important;
        // fix silly positioning
        top: 0 !important;
        left: 0 !important;
      }

      &:after {
        color: $white;
        opacity: 1 !important;
      }
    }
  }

  ::v-deep {
    .header-checkbox.custom-control.custom-checkbox.grey input:checked~label.custom-control-label::before {
      background-color: $greyPrimary !important;
      border-color: $greyPrimary !important;
    }
  }
}

.contact-details-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  max-width: 316px;
  display: flex;
  align-items: stretch;
  z-index: 99999;
  -webkit-app-region: no-drag;
}

.overlay-right-enter-active, .overlay-right-leave-active {
  transition: all .3s;
}
.overlay-right-enter, .overlay-right-leave-to {
  right: -317px;
}

::v-deep .multiselect__content {
  padding: 10px 0 !important;
}

::v-deep .multiselect .multiselect__content-wrapper .multiselect__content .multiselect__option {
  display: block !important;
  // padding: 10px 10px 10px 30px !important;

  .sb-icon {
    margin-right: 0 !important;
  }

  &.multiselect__option--selected {
    * {
      color: $blackPrimary;
    }
  }
}

::v-deep .tools-right {
  display: flex;
  margin-left: 10px;
  height: 30px;

  button:not(:first-of-type) {
    margin-left: 10px;
  }

  button {
    padding-top: 0px;
    padding-bottom: 0px;
    height: 30px;
  }
}

::v-deep .sort-dropdown,
::v-deep .filter-dropdown {
  .dropdown-toggle {
    padding: 0 16px;
    display: flex;
    align-items: center;
    font-weight: 500;
    line-height: 1;
    .sb2-icon {
      margin-right: 6px;
    }

    .sort-prefix {
      font-size: 14px;
      font-weight: 400;
      color: #54626c;
    }

    .sort-type {
      font-size: 14px;
      font-weight: 500;
      color: $blackPrimary !important;
    }
  }

  .dropdown-menu {
    width: 347px;
    overflow-y: visible;

    & > div {
      height: 100%;
    }

    h5 {
      text-align: center;
      font-size: 14px;
      font-weight: 500;
      padding: 15px;
      border-bottom: solid 1px #e0e7e9;
    }

    .content {
      padding: 10px;
      max-height: calc(100% - 48px);

      .subtitle {
        font-size: 12px;
        font-weight: 500;
        font-stretch: normal;
        color: $greyPrimary;
        margin: 8px 0 8px;
      }

      .flex-options {
        max-width: calc(50% - 5px);
      }

      .sb-icon.force-show {
        visibility: visible !important;

        &.mr-2 {
          margin-right: 6px !important;
        }
      }
    }
  }
}

::v-deep .filter-dropdown {
  &.add-y-scroll ul.dropdown-menu {
    overflow-y: auto;
  }
  .filter-button-main-content {
    color: $blackPrimary !important;
  }

  .header {
    .btn {
      padding-left: 10px !important;
      padding-right: 10px !important;
    }
    .btn.save {
      font-size: 12px;
      padding: 4px;
      position: absolute;
      top: 12px;
      right: 12px;
    }

    .btn.clear {
      font-size: 12px;
      padding: 4px;
      position: absolute;
      top: 12px;
      left: 12px;
    }
  }

  &.actions-btn {
    height: 30px;
    button {
      height: 28px;
    }
  }

  &:not(.fit-width) ul.dropdown-menu {
    width: 620px !important;
  }

  &.fit-width ul.dropdown-menu {
    width: max-content !important;
    padding-bottom: 7px !important;

    .button-dropdown-label {
      margin: 15px 10px 8px 10px;
      font-size: 12px;
      font-weight: 500;
      color: $greyPrimary;
    }

    .item {
      font-size: 14px;
      font-weight: 400;
      color: $blackPrimary;
      padding: 10px;

      &:hover {
        cursor: pointer;
        background-color: $greyUltralight;
      }
    }
  }

  ul.dropdown-menu {
    max-height: calc(100vh - 133px);

    .content {
      padding: 15px;

      .empty-icon {
        display: block;
        width: 34px;
        height: 34px;
        margin-top: 13px;
        background: url('~@/assets/img/SB-Icons/filter-empty-icon.svg');
      }

      .initial-view p {
        margin: 18px 0 0;
        font-size: 14px;
        color: $blackUltralight;
        text-align: center;
      }

      .filter-wrapper {
        display: flex;
        flex-direction: column;
      }

      .filter-row {
        display: flex;
        align-items: center;
        column-gap: 8px;

        .date-wrapper {
          column-gap: 10px;
          .down-arrow {
            position: absolute;
            top: 2px;
            right: 0;
          }
        }

        .el-date-editor {
          box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1) !important;
          border: solid 1px #cfd8dc !important;
          border-radius: 5px !important;
          width: 100% !important;
          z-index: 1;

          * {
            cursor: pointer !important;
          }

          &:hover {
            box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3) !important;
          }

          &:focus {
            outline: none !important;
            border: solid 1px #cfd8dc;
            box-shadow: 0 0 0 3px #bfdeff !important;
          }

          input {
            height: 30px;
            background-color: transparent;
            border: none !important;
            font-size: 14px;
            color: $blackPrimary;
            padding-left: 10px !important;
            padding-right: 23px !important;
          }

          .el-input__prefix {
            display: none !important;
          }

          .el-input__suffix {
            display: none !important;
          }
        }

        .close {
          cursor: pointer;
        }

        .multiselect {
          .multiselect__tags {
            padding-right: 0px;
            .multiselect__placeholder {
              padding-right: 24px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
          &.filter-type {
            max-width: 132px;
            width: 132px;

            .multiselect__single {
              display: flex;
              padding-right: 28px;
              align-items: center;

              & > p {
                margin-top: 1px !important;
              }
            }
          }
          &.filter-comparison {
            max-width: 66px;
            width: 66px;
          }
          &.filter-value {
            max-width: unset;
            width: unset;
          }
        }
      }

      .action {
        cursor: pointer;
        margin: 16px 0 8px;
        padding: 5px;
        font-size: 14px;
        font-weight: 500;
        color: $bluePrimary;
        width: fit-content;

        &:hover {
          color: #2071d1;

          .sb-icon .icon {
            background-color: #2071d1 !important;
          }
        }

        &.disabled {
          color: $blackUltralight;
          pointer-events: none;

          &:hover {
            background-color: unset;
            cursor: default;
          }

          .sb-icon .mask {
            background-color: $blackUltralight !important;
          }
        }
      }
    }
  }
}

.min-width {
  min-width: 1px;
}

::v-deep .filter-dropdown > button {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

::v-deep .manage-tags-dropdown {
  margin-left: 10px;
  .dropdown-toggle {
    .sb2-icon {
      margin-right: 6px;
    }
  }

  .filter-button-main-content {
    color: $blackPrimary !important;
  }

  &:not(.fit-width) ul.dropdown-menu {
    width: 297px !important;

    .header {
      .btn.save {
        top: 12px;
        right: 10px;
        height: 24px;
      }
      .btn.clear {
        top: 12px;
        left: 10px;
        height: 24px;
        margin-left: 0px !important;
      }
    }

    .tag-seach-wrapper {
      padding: 10px;
      background-color: $greyUltralight;
      border-bottom: solid 1px #edf0f2;
    }

    .button-dropdown-label {
      font-size: 12px;
      font-weight: 500;
      color: $greyPrimary;
      padding: 18px 10px 8px;
    }

    .tag {
      cursor: default;
      padding: 10px;
      &:hover {
        background-color: $greyUltralight;
      }
      .name {
        font-size: 14px;
        font-weight: 400;
      }
      .count {
        font-size: 11px;
        font-weight: 400;
        color: $blackUltralight;
      }
    }
  }
}

::v-deep .tag-seach-wrapper .search-wrapper {
  margin-right: 0px !important;

  input {
    // width: 100% !important;
    height: 26px !important;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .clear-text {
    top: 5px;
    right: 5px;
  }
}

.filter-operator ::v-deep {
  padding-left: 15px;
  width: fit-content;
  box-shadow: none !important;
  height: 20px;
  min-height: 20px !important;
  margin-top: 5px;
  margin-bottom: 3px;
  .multiselect__select {
    mask-size: 8px auto !important;
    margin-top: -1px !important;
    height: 20px;
  }
  .multiselect__tags {
    border: none !important;
    padding-right: 25px !important;
    min-height: 20px;
    .multiselect__single {
      font-size: 11px !important;
      font-weight: 500 !important;
      color: $blackUltralight !important;
      text-transform: uppercase !important;
    }
  }

  .multiselect__content-wrapper {
    margin-left: 9px;
    width: fit-content;
    ul li * {
      text-transform: capitalize !important;
    }
  }
}

.sb-focused-btn {
  outline: none !important;
  border: solid 1px #cfd8dc;
  box-shadow: 0 0 0 3px #bfdeff !important;
}

.clear-filters-btn {
  padding-top: 30px;
  font-size: 14px;
  font-weight: 500;
  color: $bluePrimary;

  &:hover {
    color: #1763BE;
    cursor: pointer;
  }
}

::v-deep .test span div.header-checkbox {
  display: inline-block;
  width: 20px;
}

.toggle-observer {
  width: 30px;
  height: 30px;
  background-color: red;

  &.green {
    background-color: green;
  }
}

.sb-table-area {
  .sb-table-header {
    height: 50px;
    border-bottom: solid 1px #e6ebed;
    z-index: 100;

    .checkbox-wrapper, .avatar {
      width: 40px;
    }

    .name-header {
      width: calc(25% + 30px);
      .header-checkbox {
        width: 40px;
        padding: 0px !important;
        margin: 0px !important;

        ::v-deep label.custom-control-label {
          margin: 0 0 0 13px !important;
          padding: 0 !important;
          height: 100%;

          &:before, &:after {
            height: 15px !important;
            width: 15px !important;
            // fix silly positioning
            top: 0 !important;
            left: 0 !important;
          }

          &:before {
            right: unset !important;
            top: unset !important;
            left: unset !important;
          }

          &:after {
            opacity: 1 !important;
            right: unset !important;
          }
        }
      }
    }

    .number-header {
      width: calc(20% - 10px);
    }

    .tag-header {
      width: calc(45% - 10px);
    }

    .date-header {
      width: calc(10% - 10px);
      padding-right: 9px;
      text-align: right;
    }

    span, p {
      font-size: 14px;
      font-weight: 500;
    }

    span.selected-counter {
      font-size: 14px;
      font-weight: bold;
      color: #2680eb;

      &.grey {
        color: $blackUltralight;
      }

      span {
        font-size: 14px;
        font-weight: normal;
        color: $blackUltralight;
      }
    }
  }

  .sb-table-info {
    font-size: 11px;
    font-weight: 500;
    color: $blackLight;
    padding-top: 16px !important;
    padding-bottom: 3px !important;
    cursor: default;
  }

  .sb-table-item {
    min-height: 48px;
    margin-bottom: 4px;
    border-radius: 7px;
    cursor: pointer;

    .show-on-hover {
      display: none !important;
    }

    &:hover {
      .show-on-hover {
        display: block !important;
      }

      .hide-on-hover {
        display: none !important;
      }
    }

    .show-on-checkbox-state {
      display: none !important;
    }

    .show-on-all-checkbox-state {
      display: none !important;
    }

    &.checkbox-state {
      cursor: default;
      .show-on-checkbox-state {
        display: block !important;
      }

      .hide-on-checkbox-state {
        display: none !important;
      }
    }

    .checkbox-wrapper, .avatar {
      width: 40px;
    }

    .sb-table-name-wrapper {
      width: calc(25% + 30px);

      &:hover {
        .show-on-name-hover {
          display: block !important;
        }

        .hide-on-name-hover {
          display: none !important;
        }
      }

      .custom-control.custom-checkbox {
        height: 26px !important;
      }
    }

    &.all-checkbox-state {
      cursor: default;
      .show-on-all-checkbox-state {
        display: block !important;
      }

      .hide-on-all-checkbox-state {
        display: none !important;
      }

      .sb-table-name-wrapper {
        &:hover {
          .show-on-name-hover {
            display: none !important;
          }

          .hide-on-name-hover {
            display: none !important;
          }
        }
      }
    }

    .sb-table-phone-column {
      width: calc(20% - 10px);
    }

    .sb-table-tag-column {
      width: calc(45% - 10px);
    }

    .sb-table-date-column {
      width: calc(10% - 10px);
      padding-right: 9px;
      color: #54626c;
    }

    ::v-deep .custom-control {
      &:hover {
        background-color: unset;
      }
      label.custom-control-label {
        margin: 0 0 0 8px !important;
        padding: 0 !important;
        height: 100%;

        &:before, &:after {
          height: 15px !important;
          width: 15px !important;
          // fix silly positioning
          top: 0 !important;
          left: 0 !important;
        }

        &:before {
          width: 15px !important;
          height: 15px !important;
          right: unset !important;
        }

        &:after {
          opacity: 1 !important;
          right: unset !important;
        }
      }
    }

    .show-on-hover2 {
      display: none;
    }
    .hide-on-hover2 {
      display: block;
    }

    &:hover {
      background: #fafbfc;
      cursor: default;
    }

    &:not(.key-mode-active):hover {
      cursor: pointer;
      .sb-table-date-column {
        .show-on-hover2 {
          display: block;
        }
        .hide-on-hover2 {
          display: none;
        }
      }
    }

    &.checked {
      background: #f4f8fd;
    }
  }
}

// broadcast-selector-enter-active broadcast-selector-enter-to

.broadcast-selector-content {
  position: fixed;
  bottom: 18px;
  left: calc(50% + 128.5px); // 128 is the 50% of org panel width we need to move to the right
  transform: translateX(-50%);
  border-radius: 10px;
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.27);
  opacity: 0.97;
  box-shadow: 0 1px 6px 0 rgba(48, 54, 70, 0.2);
  border: solid 1px $greyBorder;
  padding: 11px 14px 11px 20px;
  width: max-content;
  white-space: pre;

  &>span {
    font-weight: 500;
  }

  .broadcast-count {
    color: $blackUltralight;

    &.ok {
      color: $bluePrimary;
    }

    &.nok {
      color: $redPrimary;
    }
  }

  .recepients {
    color: $blackUltralight;
  }

  .FRONTAPP,
  .FRONTPACHA {
    background-image: url('~@/assets/img/SB-Icons/integration-front-color.svg');
    background-size: 14px 14px;
    background-position: center center;
    background-repeat: no-repeat;
    display: block;
    width: 18px;
    height: 18px;
  }

  #select-number {
    span {
      font-weight: normal;
    }

    .number-icon {
      height: 15px !important;
      background-size: contain !important;
      font-size: 14px !important;
      margin: 0 5px 0 7px;
      line-height: 1;
    }

    p {
      font-weight: 500;
    }
  }

  #send-broadcast {
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px $greyBorder;

    &.disabled {
      background-color: $greyUltralight;
      opacity: 1;

      ::v-deep .sb-icon span.icon.mask {
        background-color: $blackUltralight !important;
      }

      span {
        color: $blackUltralight;
      }
    }
  }
}

.broadcast-selector-enter-active, .broadcast-selector-leave-active {
  transition: all .3s;
}
.broadcast-selector-enter, .broadcast-selector-leave-to {
  bottom: -70px;
}

.broadcast-search-input-wrapper {
  padding: 10px 12px;
  background-color: $greyUltralight;
  border-bottom: solid 1px $greyChatBubble;
  position: relative;

  input {
    font-size: 14px;
    font-weight: 400;
    color: $blackUltralight;
    padding: 4px 30px 4px 8px;
    height: 28px !important;
  }

  .clear-input {
    position: absolute;
    height: 100%;
    top: 0;
    right: 20px;
  }

  ::v-deep span.icon {
    margin-right: 0;
  }
}
</style>

<style lang="scss">
.popover.interactive-popover.organisation-panel-popover.broadcast-selector-popover {
  left: 6px !important;
  top: 6px !important;
}

.popover.broadcast-limits .popover-body p {
  color: $blackUltralight;

  a {
    color: $blackUltralight;

    &:hover {
      color: $blackPrimary;
    }
  }
}

.basic-popover.fit-width {
  max-width: max-content;

  .popover-body {
    width: max-content;
  }
}
</style>
